import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { isPlatform, useIonViewWillLeave } from '@ionic/react';
import {
  IonCard,
  IonCardContent,
  IonIcon,
  IonModal,
  IonSpinner,
} from "@ionic/react";
import { close } from "ionicons/icons";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinePath, Bar } from "@visx/shape";
import { curveLinear } from "@visx/curve";
import { Group } from "@visx/group";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { extent } from "@visx/vendor/d3-array";
import { ScreenOrientation } from "@capacitor/screen-orientation";

import { getPressureFlowGraphSummaryByDateRange } from "../../services/graphs";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { useStorage } from "../../contexts/StorageContext";
import CustomDropdown from "../../components/dropdown/CustomDropDown";
import { TimelineSlider } from "./TimeLineSlider";
import { getResponsiveLayout } from "../../utils/helpers";

// -------------------- Types --------------------
type BackendItem = {
  refId: string;
  deviceSerialNumber: string;
  totalSetCycles: number;
  totalDeliveredCycles: number;
  TherapyType: string;
  therapyProfileName?: string;
  startTime: string;
  cycleData: any[];
};

type TherapyDataPoint = {
  timestamp: number;
  refId: number;
  therapyType: "auto" | "manual";
  cycle: number;
  setPausePressure: number;
  setInspPressure: number;
  setExpPressure: number;
  peakInspPressure: number; // deliveredPressure
  flow: number;
  deliveredVolume: number;
  deliveredOscillation: boolean;
  globalIndex: number;
  timeLabel: string;
  date: string;
  dateLabel: string;
  profileName?: string;
};

type TherapySession = {
  refId: number;
  therapyType: "auto" | "manual";
  therapyProfileName?: string;
  startIndex: number;
  endIndex: number;
  startTime: number;
};

type AggregationLevel = "yearly" | "monthly" | "daily" | "session";

type AggregatedStats = {
  min: number;
  max: number;
  mean: number;
};

type AggregatedDataPoint = {
  // Time identifiers
  timestamp: number; // Start of period (month/day)
  date: string; // ISO date
  dateLabel: string; // Display label
  aggregationLevel: AggregationLevel;

  // Set parameters (weighted average across all cycles)
  setPausePressure: number;
  setInspPressure: number;
  setExpPressure: number;

  // Delivered parameters (statistical aggregates)
  peakInspPressure: AggregatedStats;
  flow: AggregatedStats;
  deliveredVolume: AggregatedStats & { sum: number };

  // Metadata
  cycleCount: number;
  sessionCount: number;
  therapyTypes: string[];
  refIds: number[];

  // Original data indices for brush mapping
  startIndex: number; // Index in original data array
  endIndex: number;
};

// Union type for rendering - can be either raw cycle or aggregate
type RenderableDataPoint = TherapyDataPoint | AggregatedDataPoint;

// ========================================
// VITALS DATA TYPES
// ========================================
interface VitalsDataPoint {
  mean: number | null;
  min: number | null;
  max: number | null;
  count: number;
  daysWithData?: number;
}

interface DailyVitals {
  date: string; // "YYYY-MM-DD"
  sp02: VitalsDataPoint;
  heartRate: VitalsDataPoint;
  aqi: VitalsDataPoint;
}

interface MonthlyVitals {
  month: string; // "YYYY-MM"
  sp02: VitalsDataPoint;
  heartRate: VitalsDataPoint;
  aqi: VitalsDataPoint;
}

interface VitalsData {
  daily: DailyVitals[];
  monthly: MonthlyVitals[];
}

// Unified data structure for hover/cursor (combines therapy + vitals)
type UnifiedDataPoint = {
  date: string; // "2025-10-12"
  timestamp: number; // For xScale positioning
  hasTherapy: boolean;
  hasVitals: boolean;
  therapyPoint: RenderableDataPoint | null;
  vitalsPoint: AggregatedDataPoint | null;
};

// Update BackendResponse type to match actual API
export interface BackendResponse {
  startDate: string;
  endDate: string;
  initialData: BackendItem[];
  vitalsData: VitalsData; // ← NEW
  profileId: number;
  refIds: string[];
}

// Cache structure - stores both therapy and vitals together
interface CachedGraphData {
  therapyData: any[];
  vitalsData: VitalsData | null;
  cacheTimestamp: number;
  version: number; // For future cache migrations
}

// Type guard functions
function isAggregated(
  point: RenderableDataPoint
): point is AggregatedDataPoint {
  return "aggregationLevel" in point;
}

type TimeViewport = {
  start: Date;
  end: Date;
};

// Helper functions for date manipulation

// "YYYY-MM-DD" → local midnight. new Date("YYYY-MM-DD") parses as UTC, which is
// the previous evening in US time zones (e.g. Jan 1 → Dec 31).
const parseLocalDate = (ymd: string) => new Date(`${ymd}T00:00:00`);

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const endOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};

const addMonths = (date: Date, months: number) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export function transformTherapyData(
  backendData: BackendItem[],
  startIndex = 100
): TherapyDataPoint[] {
  const result: TherapyDataPoint[] = [];
  let globalIndex = startIndex;

  backendData.forEach((item) => {
    const therapyType = item.TherapyType.toLowerCase() as "auto" | "manual";
    const profileName = item.therapyProfileName || item.TherapyType; // ← ADD THIS

    (item.cycleData || []).forEach((cycle, idx) => {
      const dateObj = new Date(cycle.time);
      const timestamp = dateObj.getTime();

      const timeLabel = dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      const date = dateObj.toISOString().split("T")[0];

      const dateLabel = dateObj.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      result.push({
        timestamp,
        refId: Number(cycle.refId),
        therapyType,
        profileName, // ← ADD THIS
        cycle: idx + 1, // device's own sequence within the therapy
        setPausePressure: cycle.setPausePressure,
        setInspPressure: cycle.setInspPressure,
        setExpPressure: cycle.setExpPressure,
        peakInspPressure: cycle.deliveredPressure,
        flow: cycle.deliveredFlow,
        deliveredVolume: cycle.deliveredVolume,
        deliveredOscillation: cycle.oscillationStatus === 1,
        globalIndex,
        timeLabel,
        date,
        dateLabel,
      });

      globalIndex++;
    });
  });

  // Therapy position = its latest cycle, exactly as before. Only the order
  // of cycles *inside* a therapy changes: device sequence, not timestamp.
  const therapyKey = new Map<number, number>();
  result.forEach((p) => {
    const cur = therapyKey.get(p.refId);
    if (cur === undefined || p.timestamp > cur) therapyKey.set(p.refId, p.timestamp);
  });
  result.sort(
    (a, b) =>
      (therapyKey.get(b.refId) ?? 0) - (therapyKey.get(a.refId) ?? 0) ||
      a.cycle - b.cycle
  );

  // Assigned after sorting so globalIndex always matches array order.
  result.forEach((point, i) => {
    point.globalIndex = startIndex + i;
  });

  return result;
}

// Update DEFAULT_CONFIG to include measurement configuration
const DEFAULT_CONFIG = {
  xAxisMode: "both" as "both" | "mainOnly" | "volumeOnly",
  colors: {
    setPause: "rgba(168,224,95,0.6)", // #A8E05F
    setInsp: "rgba(31,183,227,0.6)", // #1FB7E3
    setExp: "rgba(255,143,28,0.6)", // #FF8F1C
    flow: "rgba(84,108,255,1)", // Bright blue
    peakInsp: "#15803d", // Dark green
    peakInspDanger: "rgba(255,36,36,1)", // Even brighter red for out-of-threshold
    volume: "#ba4bbaff", // light blue
    separator: "rgba(100,116,139,1)", // #64748b
    bg: "rgba(255,255,255,1)", // #ffffff
    missedTherapyOverlay: "rgba(4,10,0,0.03)", // Light transparent grey
    missedTherapyBorder: "rgba(200,0,0,0.3)", // Slightly darker red for borders
    cursorColor: "rgba(25, 27, 4, 1)", // Bright red
  },
  layout: {
    margins: { top: 48, right: 80, bottom: 58, left: 56 },
    mainHeight: 340,
  },
  features: {
    showThresholds: true,
    showGrid: true,
    cursorOn: { main: true, volume: true, brush: true },
    minBarWidth: 8, // Minimum bar width in pixels
    maxBarWidth: 14, // Maximum bar width in pixels
  },
  aggregation: {
    autoViewThresholds: {
      dailyMaxDays: 31,
      monthlyMaxMonths: 1,
    },
  },
  zoom: {
    enabled: true,
    mode: "discrete" as "discrete" | "continuous",
    discrete: {
      levels: ["yearly", "monthly", "daily", "session"] as AggregationLevel[],
      pinchThreshold: 0.3,
      mouseWheelThreshold: 100,
      thresholds: {
        yearlyToMonthly: 180,
        monthlyToDaily: 31,
        dailyToSession: 1,
      },
    },
    animation: {
      duration: 0,
      enabled: false,
    },
    indicator: {
      position: "header" as "header" | "top-right" | "bottom",
      alwaysVisible: true,
    },
  },
  peakPressureThresholds: {
    enabled: true,
    setThreshold: 5,
    tolerance: 3,
    highlightMode: "none" as "both" | "high" | "low" | "none",
  },
  // ========================================
  // NEW: MEASUREMENT CONFIGURATION
  // ========================================
  measurements: {
    none: {
      label: "None",
      unit: "",
      scale: [0, 100] as [number, number],
      color: "#000000",
      isVitals: false,
      needsFlip: false,
    },
    flow: {
      label: "Exp. Flow",
      unit: "L/min",
      scale: [0, 100] as [number, number], // Dynamic - computed from flowExtent
      color: "rgba(84,108,255,1)", // Same as colors.flow
      isVitals: false,
      needsFlip: true, // Special handling - align with left axis
    },
    volume: {
      label: "Exp. Volume",
      unit: "mL",
      scale: [0, 1000] as [number, number], // Dynamic - computed from volumeExtent
      color: "#ba4bbaff", // Same as colors.volume
      isVitals: false,
      needsFlip: true, // Special handling - align with left axis
    },
    // Vitals - no flipping needed
    sp02: {
      label: "SpO2",
      unit: "%",
      scale: [85, 100] as [number, number],
      color: "#10b981", // Green
      isVitals: true,
      needsFlip: false,
    },
    heartRate: {
      label: "Heart Rate",
      unit: "BPM",
      scale: [40, 180] as [number, number],
      color: "#ef4444", // Red
      isVitals: true,
      needsFlip: false,
    },
    aqi: {
      label: "AQI",
      unit: "AQI",
      scale: [0, 5] as [number, number],
      color: "#f59e0b", // Amber
      isVitals: true,
      needsFlip: false,
    },
  },
} as const;

// Derive types from config
export type MeasurementType = keyof typeof DEFAULT_CONFIG.measurements;

// Generate dropdown options from config
export const MEASUREMENT_OPTIONS = Object.entries(
  DEFAULT_CONFIG.measurements
).map(([value, config]) => ({
  label: config.label,
  value: value,
  // colorClass: undefined,
}));

// Helper functions - now reference DEFAULT_CONFIG.measurements
const getFieldConfig = (field: MeasurementType) => {
  return DEFAULT_CONFIG.measurements[field];
};

const isVitalsParameter = (field: MeasurementType): boolean => {
  return DEFAULT_CONFIG.measurements[field].isVitals;
};

const needsFlip = (field: MeasurementType): boolean => {
  return DEFAULT_CONFIG.measurements[field].needsFlip;
};
// Parse vitals date string to UTC timestamp (noon to avoid timezone issues)
const parseVitalsTimestamp = (
  dateStr: string,
  aggregationLevel: AggregationLevel
): number => {
  if (aggregationLevel === "yearly") {
    // Monthly data: "2025-11" → Start of month at midnight local time
    return new Date(dateStr + "-01T00:00:00").getTime();
  } else {
    // Daily data: "2025-11-06" → Start of day at midnight local time
    return new Date(dateStr + "T00:00:00").getTime();
  }
};

// -------------------- Component Props --------------------
interface TherapyChartProps {
  profileId: number;
  dateRange: { startDate: string; endDate: string };
  onCurrentTherapyChange?: (refId: string) => void;
  config?: Partial<typeof DEFAULT_CONFIG>;
}

interface SessionSegment {
  session: TherapySession;
  cycleIndices: number[]; // indices in visibleData
  startX: number; // pixel position in chart
  endX: number; // pixel position in chart
  width: number; // width of this segment
  gapAfter: number; // gap width after this segment
}

const TherapyChart: React.FC<TherapyChartProps> = ({
  profileId,
  dateRange,
  onCurrentTherapyChange,
  config = {},
}) => {
  // Merge default config with user overrides
  const mergedConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
      colors: { ...DEFAULT_CONFIG.colors, ...(config as any).colors },
      layout: { ...DEFAULT_CONFIG.layout, ...(config as any).layout },
      features: { ...DEFAULT_CONFIG.features, ...(config as any).features },
    }),
    [config]
  );

  // -------------------- Local state --------------------
  const [rawData, setRawData] = useState<BackendItem[] | null>(null);
  const [data, setData] = useState<TherapyDataPoint[]>([]);
  const [vitalsData, setVitalsData] = useState<VitalsData | null>(null);
  const [therapySessions, setTherapySessions] = useState<TherapySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tooltip / cursor (index into visibleData)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const volumeOverlayRef = useRef<SVGRectElement | null>(null);
  const mainGraphOverlayRef = useRef<SVGRectElement | null>(null);

  const handleOverlayRef = (node: SVGRectElement | null) => {
    mainGraphOverlayRef.current = node;
  };

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [aggregationLevel, setAggregationLevel] =
    useState<AggregationLevel>("daily");
  const [yearlyData, setYearlyData] = useState<AggregatedDataPoint[]>([]);
  const [monthlyData, setMonthlyData] = useState<AggregatedDataPoint[]>([]);

  //zoom states:
  const zoomStateRef = useRef<{
    initialDistance: number;
    initialLevel: AggregationLevel;
    accumulatedDelta: number;
    anchorX: number;
    hasZoomedThisGesture: boolean; // NEW FLAG
  } | null>(null);
  const wheelDeltaRef = useRef<number>(0);
  const isTimelineSyncingRef = useRef(false);
  const [isZooming, setIsZooming] = useState(false);

  const [currentSessionRefId, setCurrentSessionRefId] = useState<number | null>(
    null
  );

  // Timeline navigation state
  const [currentContext, setCurrentContext] = useState<Date>(new Date());
  const [selectedTimelineIndex, setSelectedTimelineIndex] = useState<
    number | null
  >(null);

  const isSmallScreen = window.innerWidth < 600;
  // daily data is the original 'data' array

  // Determine which dataset to use for rendering
  const [activeData, setActiveData] = useState<RenderableDataPoint[]>([]);

  // Determine initial view based on date range
  const determineInitialView = useCallback(
    (startDate: string, endDate: string): AggregationLevel => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff <= 31) {
        return "daily"; // Show all cycles
      } else if (daysDiff <= 31 * 6) {
        // ~6 months
        return "monthly"; // Show day aggregates
      } else {
        return "yearly"; // Show month aggregates
      }
    },
    []
  );

  const { storage, isStorageReady } = useStorage();

  // Add state for full calendar bounds
  const [fullCalendarBounds, setFullCalendarBounds] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  // Viewport controls what time range is visible in main/volume charts
  const [viewport, setViewport] = useState<TimeViewport | null>(null);

  // Dragging state for pan interaction
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; viewport: TimeViewport } | null>(
    null
  );

  // Store chart width for drag calculations
  const chartWidthRef = useRef<number>(0);

  // Unified pointer handler (mouse + touch)
  const handleVolumePointerMove = (ev: MouseEvent | TouchEvent) => {
    let clientX: number;
    if ("touches" in ev) {
      if (ev.touches.length === 0) return;
      clientX = ev.touches[0].clientX;
    } else {
      clientX = ev.clientX;
    }

    // get overlay bounds relative to viewport
    const overlayEl = volumeOverlayRef.current;
    if (!overlayEl) return;

    const rect = overlayEl.getBoundingClientRect();
    const margins = mergedConfig.layout.margins;
    // adjust for margins and clamp
    const x = clientX - rect.left - margins.left; // ✅ fix offset
    const usableWidth = rect.width - margins.left - margins.right;
    const ratio = Math.max(0, Math.min(1, x / usableWidth)); // ✅ clamp safely

    const idxVisible = Math.round(ratio * (visibleData.length - 1));

    setHoverIndex(idxVisible);
    // setHoverIndexGlobal(gIdx);
    setIsHovering(true);
  };

  // ==================== CALENDAR BOUNDS CALCULATION ====================
  const calculateCalendarBounds = useCallback(
    (data: TherapyDataPoint[], level: AggregationLevel) => {
      if (!data || data.length === 0) return null;

      const firstPoint = data[0];
      const lastPoint = data[data.length - 1];
      const firstDate = new Date(firstPoint.timestamp);
      const lastDate = new Date(lastPoint.timestamp);

      const firstYear = firstDate.getFullYear();
      const lastYear = lastDate.getFullYear();

      if (level === "yearly") {
        // 🟢 Full span: Jan 1 of first year → Dec 31 of last year
        return {
          start: new Date(firstYear, 0, 1, 0, 0, 0, 0),
          end: new Date(lastYear, 11, 31, 23, 59, 59, 999),
        };
      }

      // Daily/monthly: navigation is limited to the period the user selected
      // (week / month / year), not to the first..last day that has data —
      // otherwise swipes clamp back onto data days and empty days are unreachable.
      return {
        start: startOfDay(parseLocalDate(dateRange.startDate)),
        end: endOfDay(parseLocalDate(dateRange.endDate)),
      };
    },
    [dateRange.startDate, dateRange.endDate]
  );

  // Update full calendar bounds when data or aggregation changes
  useEffect(() => {
    if (data && data.length > 0) {
      const bounds = calculateCalendarBounds(data, aggregationLevel);
      setFullCalendarBounds(bounds);
    }
  }, [data, aggregationLevel, calculateCalendarBounds]);

  // ==================== VIEWPORT INITIALIZATION ====================

  // Initialize viewport when aggregation level or data changes
  // ==================== UPDATED VIEWPORT INITIALIZATION ====================

  const initializeViewport = useCallback(
    (level: AggregationLevel, dataPoints: TherapyDataPoint[]) => {
      if (!dataPoints || dataPoints.length === 0) {
        setViewport(null);
        return;
      }

      const lastPoint = dataPoints[dataPoints.length - 1];
      const lastDate = new Date(lastPoint.timestamp);

      if (level === "yearly") {
        // Show entire year(s) from first to last data
        const bounds = calculateCalendarBounds(dataPoints, level);
        if (bounds) {
          setViewport({ start: bounds.start, end: bounds.end });
        }
      } else if (level === "monthly") {
        // Show all 31 days of first month with data
        const lastMonth = new Date(
          lastDate.getFullYear(),
          lastDate.getMonth(),
          1
        );
        const lastDayOfMonth = new Date(
          lastDate.getFullYear(),
          lastDate.getMonth() + 1,
          0
        );
        setViewport({
          start: lastMonth,
          end: new Date(
            lastDayOfMonth.getFullYear(),
            lastDayOfMonth.getMonth(),
            lastDayOfMonth.getDate(),
            23,
            59,
            59,
            999
          ),
        });
      } else {
        // Daily: Show first day with data (full 24 hours)
        const lastDay = startOfDay(lastDate);
        setViewport({
          start: lastDay,
          end: endOfDay(lastDay),
        });
      }
    },
    [calculateCalendarBounds]
  );

  const [selectedMetric, setSelectedMetric] = useState<MeasurementType>("flow");

  // Fullscreen modal flag
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Ref for latest data to avoid stale closures
  const dataRef = useRef<TherapyDataPoint[]>([]);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // -------------------- Fetching data --------------------
  useEffect(() => {
    let mounted = true;

    // Utility to shallow-compare arrays of data points
    const areDataEqual = (a: any[], b: any[]): boolean => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;

      for (let i = 0; i < a.length; i++) {
        const x = a[i];
        const y = b[i];
        // Compare key fields that affect rendering
        if (
          x.timestamp !== y.timestamp ||
          x.flow !== y.flow ||
          x.refId !== y.refId ||
          x.setInspPressure !== y.setInspPressure ||
          x.setExpPressure !== y.setExpPressure ||
          x.setPausePressure !== y.setPausePressure
        ) {
          return false;
        }
      }
      return true;
    };

    // NEW: Utility to compare vitals data
    const areVitalsEqual = (
      a: VitalsData | null,
      b: VitalsData | null
    ): boolean => {
      if (!a && !b) return true;
      if (!a || !b) return false;

      // Quick check - compare lengths
      if (a.daily.length !== b.daily.length) return false;
      if (a.monthly.length !== b.monthly.length) return false;

      // Deep check - compare first and last entries
      if (a.daily.length > 0) {
        const aFirst = a.daily[0];
        const bFirst = b.daily[0];
        if (aFirst.date !== bFirst.date) return false;

        const aLast = a.daily[a.daily.length - 1];
        const bLast = b.daily[b.daily.length - 1];
        if (aLast.date !== bLast.date) return false;
      }

      return true;
    };

    const processTherapyData = (
      sortedData: any[],
      vitals: VitalsData | null, // ← NEW parameter
      source: "cache" | "server"
    ) => {
      if (!mounted) return;

      setRawData(sortedData);
      setData(sortedData);
      setVitalsData(vitals); // ← NEW: Store vitals data in state

      // 1️⃣ Build session mapping
      const sessionsMap: Record<number, TherapySession> = {};
      sortedData.forEach((p, idx) => {
        if (!sessionsMap[p.refId]) {
          sessionsMap[p.refId] = {
            refId: p.refId,
            therapyType: p.therapyType,
            therapyProfileName: p.therapyProfileName,
            startIndex: idx,
            endIndex: idx,
            startTime: p.timestamp,
          };
        } else {
          sessionsMap[p.refId].endIndex = idx;
        }
      });
      setTherapySessions(Object.values(sessionsMap));

      // 2️⃣ Compute aggregated data
      if (sortedData.length > 0) {
        const yearly = aggregateData(sortedData, "yearly");
        const monthly = aggregateData(sortedData, "monthly");

        setYearlyData(yearly);
        setMonthlyData(monthly);

        // 3️⃣ Determine initial view
        const initialView = determineInitialView(
          dateRange.startDate,
          dateRange.endDate
        );
        setAggregationLevel(initialView);

        // 4️⃣ Set active data and initialize viewport
        if (initialView === "yearly") {
          setActiveData(yearly);
          initializeViewport("yearly", sortedData);
        } else if (initialView === "monthly") {
          setActiveData(monthly);
          initializeViewport("monthly", sortedData);
        } else {
          setActiveData(sortedData);
          initializeViewport("daily", sortedData);
        }

        if (onCurrentTherapyChange) {
          onCurrentTherapyChange(
            String(sortedData[sortedData.length - 1].refId)
          );
        }
      }
      setLoading(false);
    };

    const fetchAndCacheData = async () => {
      setLoading(true);
      setError(null);

      const cacheKey = `pressureFlowGraphData_${profileId}_${dateRange.startDate}_${dateRange.endDate}`;
      const CACHE_VERSION = 1; // Increment this to invalidate old caches

      try {
        let cachedTherapyData: any[] | null = null;
        let cachedVitalsData: VitalsData | null = null;

        // 1️⃣ Try cached data first
        if (storage && isStorageReady) {
          const cached: CachedGraphData | any[] | null = await getIonicStorage(
            storage,
            cacheKey
          );

          // Handle both new cache format and legacy format
          if (cached) {
            if (Array.isArray(cached)) {
              // Legacy cache format (just therapy array)
              // console.log("📦 Legacy cache detected - will migrate");
              cachedTherapyData = cached;
              cachedVitalsData = null;
            } else if (cached.version === CACHE_VERSION) {
              // New cache format
              cachedTherapyData = cached.therapyData;
              cachedVitalsData = cached.vitalsData;
            } else {
              // Old cache version - ignore and fetch fresh
              // console.log("📦 Old cache version - ignoring cached data");
            }

            if (cachedTherapyData && cachedTherapyData.length > 0) {
              processTherapyData(cachedTherapyData, cachedVitalsData, "cache");
            }
          }
        }

        // 2️⃣ Fetch fresh data from server
        const res: BackendResponse =
          await getPressureFlowGraphSummaryByDateRange(
            profileId,
            dateRange.startDate,
            dateRange.endDate
          );

        const backendItems: BackendItem[] = res?.initialData ?? [];
        const serverVitalsData: VitalsData = res?.vitalsData ?? {
          daily: [],
          monthly: [],
        };

        // Transform therapy data
        // Already ordered by transformTherapyData — same as Day view.
        const sorted = transformTherapyData(backendItems, 100);

        if (!mounted) return;

        // 3️⃣ Process fresh data
        processTherapyData(sorted, serverVitalsData, "server");

        // 4️⃣ Conditionally update cache
        if (storage && isStorageReady) {
          const therapyChanged =
            !cachedTherapyData || !areDataEqual(cachedTherapyData, sorted);
          const vitalsChanged = !areVitalsEqual(
            cachedVitalsData,
            serverVitalsData
          );

          if (therapyChanged || vitalsChanged) {
            const dataToCache: CachedGraphData = {
              therapyData: sorted,
              vitalsData: serverVitalsData,
              cacheTimestamp: Date.now(),
              version: CACHE_VERSION,
            };

            await ionicStorage(storage, cacheKey, dataToCache);
          } else {
            // console.log("✨ Cache unchanged — skipping write");
          }
        }
      } catch (err: any) {
        console.error("❌ Failed to fetch data:", err);
        setError(
          "Unable to update therapy data right now. Displaying the most recent available data. Please check your internet connection."
        );
        setLoading(false);
        // Continue showing cached data silently
      }
    };

    fetchAndCacheData();

    return () => {
      mounted = false;
    };
  }, [
    profileId,
    dateRange.startDate,
    dateRange.endDate,
    onCurrentTherapyChange,
    determineInitialView,
    initializeViewport,
  ]);

  // Recompute on orientation/resize to avoid brush sensitivity loss
  useEffect(() => {
    const onResize = () => {
      // force rerender to recompute ParentSize internals
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // -------------------- Derived visible data --------------------
  const visibleData = useMemo(() => {
    if (!viewport) return [];

    const { start, end } = viewport;

    // Session view: filter by both time AND refId
    if (aggregationLevel === "session") {
      if (currentSessionRefId === null) {
        // console.log("❌ Session view but no currentSessionRefId");
        return [];
      }

      const filtered = data.filter((point) => {
        const matchesRefId = point.refId === currentSessionRefId;
        const matchesTime =
          point.timestamp >= start.getTime() &&
          point.timestamp <= end.getTime();

        return matchesRefId && matchesTime;
      });

      return filtered;
    }

    // Daily view: use full 'data' array and filter by time only
    if (aggregationLevel === "daily") {
      const filtered = data.filter((point) => {
        return (
          point.timestamp >= start.getTime() && point.timestamp <= end.getTime()
        );
      });
      return filtered;
    }

    // For aggregated views (yearly/monthly), use activeData which is pre-aggregated
    if (!activeData || activeData.length === 0) return [];

    const filtered = activeData.filter((point) => {
      const timestamp = isAggregated(point) ? point.timestamp : point.timestamp;
      return timestamp >= start.getTime() && timestamp <= end.getTime();
    });

    return filtered;
  }, [data, activeData, viewport, aggregationLevel, currentSessionRefId]);

  // Merge therapy and vitals data for unified hover/cursor
  const mergeTherapyAndVitals = useCallback(
    (
      therapyData: RenderableDataPoint[],
      vitalsData: RenderableDataPoint[]
    ): UnifiedDataPoint[] => {
      const dateMap = new Map<string, UnifiedDataPoint>();

      // Add therapy points
      therapyData.forEach((point) => {
        const date = isAggregated(point)
          ? (point as AggregatedDataPoint).date
          : new Date(point.timestamp).toISOString().split("T")[0];

        dateMap.set(date, {
          date,
          timestamp: point.timestamp,
          hasTherapy: true,
          hasVitals: false,
          therapyPoint: point,
          vitalsPoint: null,
        });
      });

      // Add or merge vitals points
      vitalsData.forEach((point) => {
        const aggPoint = point as AggregatedDataPoint;
        const existing = dateMap.get(aggPoint.date);

        if (existing) {
          // Date has both therapy and vitals
          existing.hasVitals = true;
          existing.vitalsPoint = aggPoint;
        } else {
          // Date has only vitals
          dateMap.set(aggPoint.date, {
            date: aggPoint.date,
            timestamp: aggPoint.timestamp,
            hasTherapy: false,
            hasVitals: true,
            therapyPoint: null,
            vitalsPoint: aggPoint,
          });
        }
      });

      // Convert to sorted array
      return Array.from(dateMap.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
    },
    []
  );

  // Check if viewport has any data
  const hasDataInViewport = visibleData.length > 0;

  // Check if there's vitals data in the current viewport
  const hasVitalsInViewport = useMemo(() => {
    if (!vitalsData || !viewport || !isVitalsParameter(selectedMetric)) {
      return false;
    }

    const minTime = viewport.start.getTime();
    const maxTime = viewport.end.getTime();

    if (aggregationLevel === "yearly") {
      // Check monthly vitals
      return vitalsData.monthly.some((item) => {
        const timestamp = parseVitalsTimestamp(item.month, aggregationLevel);
        if (timestamp < minTime || timestamp > maxTime) return false;

        const vitalData = item[
          selectedMetric as keyof MonthlyVitals
        ] as VitalsDataPoint;
        return vitalData.min !== null && vitalData.max !== null;
      });
    } else {
      // Check daily vitals
      return vitalsData.daily.some((item) => {
        const timestamp = parseVitalsTimestamp(item.date, aggregationLevel);
        if (timestamp < minTime || timestamp > maxTime) return false;

        const vitalData = item[
          selectedMetric as keyof DailyVitals
        ] as VitalsDataPoint;
        return vitalData.min !== null && vitalData.max !== null;
      });
    }
  }, [vitalsData, viewport, selectedMetric, aggregationLevel]);

  // ==================== DRAG/PAN HANDLERS ====================

  // Handle drag start (mouse or touch)
  const handleDragStart = useCallback(
    (clientX: number) => {
      if (!viewport) return;
      setIsDragging(true);
      dragStartRef.current = {
        x: clientX,
        viewport: { ...viewport },
      };
    },
    [viewport]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // ==================== UPDATE EXTENT CALCULATIONS ====================
  // Update pressure extent to handle both aggregated and raw data
  const pressureExtent = useMemo(() => {
    if (!visibleData.length) return [-50, 50];

    const values: number[] = [];

    visibleData.forEach((d) => {
      values.push(
        isAggregated(d) ? d.setPausePressure : d.setPausePressure,
        isAggregated(d) ? d.setInspPressure : d.setInspPressure,
        isAggregated(d) ? d.setExpPressure : d.setExpPressure
      );

      if (isAggregated(d)) {
        values.push(d.peakInspPressure.min, d.peakInspPressure.max);
      } else {
        values.push(d.peakInspPressure);
      }
    });

    return extent(values) as [number, number];
  }, [visibleData]);

  // Update flow extent
  const flowExtent = useMemo(() => {
    if (!visibleData.length) {
      // console.log("⚠️ No visibleData for flow extent");
      return [0, 1];
    }

    const values: number[] = [];

    visibleData.forEach((d) => {
      if (isAggregated(d)) {
        values.push(d.flow.min, d.flow.max);
      } else {
        values.push(d.flow);
      }
    });

    const ext = extent(values) as [number, number];
    // console.log("📈 Flow extent:", ext, "from", values.length, "values");
    return ext;
  }, [visibleData]);
  // Update volume extent
  const volumeExtent = useMemo(() => {
    if (!visibleData.length) {
      // console.log("⚠️ No visibleData for volume extent");
      return [0, 1];
    }

    const values: number[] = [];

    visibleData.forEach((d) => {
      if (isAggregated(d)) {
        values.push(d.deliveredVolume.min, d.deliveredVolume.max);
      } else if (
        d.deliveredVolume !== undefined &&
        d.deliveredVolume !== null
      ) {
        values.push(d.deliveredVolume);
      }
    });

    const ext =
      values.length > 0 ? (extent(values) as [number, number]) : [0, 1];
    // console.log("📊 Volume extent:", ext, "from", values.length, "values");
    return ext;
  }, [visibleData]);

  // Vitals extent - compute from ALL vitals data in date range (not just visible)
  const vitalsExtent = useMemo(() => {
    if (
      !vitalsData ||
      selectedMetric === "none" ||
      !isVitalsParameter(selectedMetric)
    ) {
      return null;
    }

    // Use viewport instead of dateRange
    if (!viewport) return null;

    const minTime = viewport.start.getTime();
    const maxTime = viewport.end.getTime();

    // Extract min/max values for selected metric
    const values: number[] = [];

    if (aggregationLevel === "yearly") {
      // Handle monthly data
      vitalsData.monthly.forEach((item) => {
        const timestamp = parseVitalsTimestamp(item.month, aggregationLevel);

        if (timestamp >= minTime && timestamp <= maxTime) {
          const vitalData = item[
            selectedMetric as keyof MonthlyVitals
          ] as VitalsDataPoint;
          if (vitalData.min !== null) values.push(vitalData.min);
          if (vitalData.max !== null) values.push(vitalData.max);
        }
      });
    } else {
      // Handle daily data
      vitalsData.daily.forEach((item) => {
        const timestamp = parseVitalsTimestamp(item.date, aggregationLevel);

        if (timestamp >= minTime && timestamp <= maxTime) {
          const vitalData = item[
            selectedMetric as keyof DailyVitals
          ] as VitalsDataPoint;
          if (vitalData.min !== null) values.push(vitalData.min);
          if (vitalData.max !== null) values.push(vitalData.max);
        }
      });
    }

    if (values.length === 0) {
      // console.log(`⚠️ No vitals data for ${selectedMetric} in date range`);
      return null;
    }

    const ext = extent(values) as [number, number];
    return ext;
  }, [vitalsData, selectedMetric, aggregationLevel, dateRange]);

  // ==================== AGGREGATION UTILITIES ====================

  // Helper: Calculate weighted average
  function weightedAverage(values: number[], weights: number[]): number {
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    if (totalWeight === 0) return 0;
    const weightedSum = values.reduce(
      (sum, val, i) => sum + val * weights[i],
      0
    );
    return weightedSum / totalWeight;
  }

  // Helper: Calculate stats
  function calculateStats(values: number[]): AggregatedStats {
    if (values.length === 0) return { min: 0, max: 0, mean: 0 };
    return {
      min: Math.min(...values),
      max: Math.max(...values),
      mean: values.reduce((sum, v) => sum + v, 0) / values.length,
    };
  }

  // Helper: Group data by time period
  function groupByPeriod(
    data: TherapyDataPoint[],
    level: AggregationLevel
  ): Record<string, TherapyDataPoint[]> {
    const grouped: Record<string, TherapyDataPoint[]> = {};

    data.forEach((point) => {
      const date = new Date(point.timestamp);
      let key: string;

      switch (level) {
        case "yearly":
          // Group by month: "2025-01"
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
          break;
        case "monthly":
          // Group by day: "2025-01-15"
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(date.getDate()).padStart(2, "0")}`;
          break;
        case "daily":
          // No grouping - return original data
          return;
        default:
          key = point.date;
      }

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(point);
    });

    return grouped;
  }

  // Main aggregation function
  function aggregateData(
    data: TherapyDataPoint[],
    level: AggregationLevel
  ): AggregatedDataPoint[] {
    if (level === "daily") {
      // No aggregation needed for daily view
      return [];
    }

    const grouped = groupByPeriod(data, level);
    const aggregated: AggregatedDataPoint[] = [];

    Object.entries(grouped).forEach(([key, points]) => {
      if (points.length === 0) return;

      // Sort points by index to get start/end
      const sortedPoints = [...points].sort(
        (a, b) => a.globalIndex - b.globalIndex
      );

      // Calculate weighted average set pressures
      const weights = points.map(() => 1); // Equal weight per cycle
      const avgSetPause = weightedAverage(
        points.map((p) => p.setPausePressure),
        weights
      );
      const avgSetInsp = weightedAverage(
        points.map((p) => p.setInspPressure),
        weights
      );
      const avgSetExp = weightedAverage(
        points.map((p) => p.setExpPressure),
        weights
      );

      // Calculate delivered parameter stats
      const peakPressures = points.map((p) => p.peakInspPressure);
      const flows = points.map((p) => p.flow);
      const volumes = points.map((p) => p.deliveredVolume);

      // Create date label based on level
      const firstPoint = sortedPoints[0];
      const date = new Date(firstPoint.timestamp);
      let dateLabel: string;
      let timestamp: number;

      if (level === "yearly") {
        // Month label: "Jan 2025"
        dateLabel = date.toLocaleDateString(undefined, {
          month: "short",
          year: "numeric",
        });
        // Timestamp = start of month
        timestamp = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
      } else {
        // Day label: "Jan 15, 2025"
        dateLabel = date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        // Timestamp = start of day
        timestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        ).getTime();
      }

      aggregated.push({
        timestamp,
        date: key,
        dateLabel,
        aggregationLevel: level,

        // Rounded to 2 decimals (will show as 24.60 or 24.6)
        setPausePressure: Math.round(avgSetPause * 100) / 100,
        setInspPressure: Math.round(avgSetInsp * 100) / 100,
        setExpPressure: Math.round(avgSetExp * 100) / 100,

        peakInspPressure: calculateStats(peakPressures),
        flow: calculateStats(flows),
        deliveredVolume: {
          ...calculateStats(volumes),
          sum: volumes.reduce((sum, v) => sum + v, 0),
        },

        cycleCount: points.length,
        sessionCount: new Set(points.map((p) => p.refId)).size,
        therapyTypes: Array.from(new Set(points.map((p) => p.therapyType))),
        refIds: Array.from(new Set(points.map((p) => p.refId))),

        startIndex: sortedPoints[0].globalIndex,
        endIndex: sortedPoints[sortedPoints.length - 1].globalIndex,
      });
    });

    // Sort by timestamp
    return aggregated.sort((a, b) => a.timestamp - b.timestamp);
  }

  // ==================== UPDATED RESET BUTTON (FULL CALENDAR RESET) ====================

  // Zoom to next/previous level centered on a specific timestamp
  const zoomToLevel = useCallback(
    (targetLevel: AggregationLevel, centerTimestamp: Date) => {
      if (!data || data.length === 0) return;
      setIsZooming(true);
      setAggregationLevel(targetLevel);

      let newStart: Date | undefined = undefined;
      let newEnd: Date | undefined = undefined;

      if (targetLevel === "yearly") {
        const bounds = calculateCalendarBounds(data, targetLevel);
        if (bounds) {
          newStart = bounds.start;
          newEnd = bounds.end;
        } else {
          return;
        }
        setActiveData(yearlyData);
        setCurrentSessionRefId(null);
        setSelectedTimelineIndex(null);
        setSelectedMonth(null);
        setSelectedDay(null);
      } else if (targetLevel === "monthly") {
        newStart = startOfMonth(centerTimestamp);
        newEnd = endOfMonth(centerTimestamp);
        setActiveData(monthlyData);

        let startIdx = 0;
        let endIdx = monthlyData.length - 1;
        for (let i = 0; i < monthlyData.length; i++) {
          const pointDate = new Date(monthlyData[i].timestamp);
          if (
            pointDate.getMonth() === centerTimestamp.getMonth() &&
            pointDate.getFullYear() === centerTimestamp.getFullYear()
          ) {
            if (startIdx === 0) startIdx = i;
            endIdx = i;
          }
        }
        setCurrentSessionRefId(null);
        setSelectedTimelineIndex(null);
      } else if (targetLevel === "daily") {
        newStart = startOfDay(centerTimestamp);
        newEnd = endOfDay(centerTimestamp);
        setActiveData(data);

        let startIdx = -1;
        let endIdx = -1;
        for (let i = 0; i < data.length; i++) {
          if (
            data[i].timestamp >= newStart.getTime() &&
            data[i].timestamp <= newEnd.getTime()
          ) {
            if (startIdx === -1) startIdx = i;
            endIdx = i;
          }
        }
        setCurrentSessionRefId(null);
        setSelectedTimelineIndex(null);
      } else if (targetLevel === "session") {
        // Find which session contains the centerTimestamp
        const sessionsInDay = therapySessions.filter((s) => {
          const sessionDate = new Date(s.startTime);
          return (
            sessionDate.getDate() === centerTimestamp.getDate() &&
            sessionDate.getMonth() === centerTimestamp.getMonth() &&
            sessionDate.getFullYear() === centerTimestamp.getFullYear()
          );
        });

        if (sessionsInDay.length === 0) return;

        // Find closest session to center timestamp
        let targetSession = sessionsInDay[0];
        let minDiff = Math.abs(
          new Date(sessionsInDay[0].startTime).getTime() -
          centerTimestamp.getTime()
        );

        sessionsInDay.forEach((s) => {
          const diff = Math.abs(
            new Date(s.startTime).getTime() - centerTimestamp.getTime()
          );
          if (diff < minDiff) {
            minDiff = diff;
            targetSession = s;
          }
        });

        setCurrentSessionRefId(targetSession.refId);

        // Set viewport to the session's day
        newStart = startOfDay(new Date(targetSession.startTime));
        newEnd = endOfDay(new Date(targetSession.startTime));
        setActiveData(data);
      }

      if (newStart && newEnd) {
        setViewport({ start: newStart, end: newEnd });
      }

      setTimeout(() => {
        setIsZooming(false);
      }, 300);
    },
    [data, yearlyData, monthlyData, therapySessions, calculateCalendarBounds]
  );

  // FIXED Issue #3: Don't navigate back to current date at limits
  const handleScrollTimeline = useCallback(
    (direction: "left" | "right") => {
      const daysToScroll = 7;
      const newContext = new Date(currentContext);

      if (direction === "left") {
        newContext.setDate(currentContext.getDate() - daysToScroll);
      } else {
        newContext.setDate(currentContext.getDate() + daysToScroll);
      }

      // Check if we're within data range
      if (dateRange.startDate && dateRange.endDate) {
        if (newContext < parseLocalDate(dateRange.startDate)) {
          return; // At start limit
        }
        if (newContext > parseLocalDate(dateRange.endDate)) {
          return; // At end limit
        }
      }

      setCurrentContext(newContext);
    },
    [currentContext, dateRange]
  );

  // ==================== TIMELINE NAVIGATION HANDLERS ====================

  const handleTimelineItemClick = useCallback(
    (item: any, index: number) => {
      const clickedDate = new Date(item.timestamp);

      switch (aggregationLevel) {
        case "yearly": {
          // FIXED Issue 1: Clicking month in yearly view
          const monthStart = new Date(
            clickedDate.getFullYear(),
            clickedDate.getMonth(),
            1
          );
          const clickedMonth = clickedDate.getMonth();

          if (selectedMonth === clickedMonth) {
            // Same month clicked again → Deselect and stay in yearly
            setSelectedMonth(null);
            // Stay in yearly view, just deselect
          } else {
            // Different month → Zoom to monthly view
            setSelectedTimelineIndex(null);
            setSelectedDay(null);
            setSelectedMonth(clickedMonth); // Track selected month
            setCurrentContext(monthStart);
            zoomToLevel("monthly", monthStart);
          }
          break;
        }

        case "monthly": {
          // FIXED Req 1: Monthly view - clicking any date zooms to daily
          // No more zoom to yearly since we deselect on zoom out from daily

          if (
            selectedDay &&
            clickedDate.toDateString() === selectedDay.toDateString()
          ) {
            // Same day clicked → Zoom back to daily (Option B)
            setSelectedDay(clickedDate);
            setSelectedTimelineIndex(index);
            setSelectedMonth(null);
            setCurrentContext(clickedDate);
            zoomToLevel("daily", clickedDate);
          } else {
            // Different day → Zoom to daily
            setSelectedDay(clickedDate);
            setSelectedTimelineIndex(index);
            setSelectedMonth(null);
            setCurrentContext(clickedDate);
            zoomToLevel("daily", clickedDate);
          }
          break;
        }

        case "daily": {
          // FIXED Req 1: Daily → Monthly with deselection
          if (
            selectedDay &&
            clickedDate.toDateString() === selectedDay.toDateString()
          ) {
            // Check date range threshold
            if (dateRange.startDate && dateRange.endDate) {
              const rangeStart = new Date(dateRange.startDate);
              const rangeEnd = new Date(dateRange.endDate);
              const daysDiff = Math.ceil(
                (rangeEnd.getTime() - rangeStart.getTime()) /
                (1000 * 60 * 60 * 24)
              );
              if (daysDiff < 32) {
                // console.log(
                //   "⚠️ Date range too small (<32 days) - preventing zoom out"
                // );
                return;
              }
            }

            // console.log("✓ Zooming out to monthly - showing full month");

            // Zoom out to MONTHLY view - show entire month
            const monthStart = new Date(
              clickedDate.getFullYear(),
              clickedDate.getMonth(),
              1
            );

            // DESELECT everything when zooming out
            setSelectedDay(null);
            setSelectedTimelineIndex(null);
            setSelectedMonth(null);
            setCurrentContext(monthStart);
            zoomToLevel("monthly", monthStart);
          } else {
            // Different day → Switch to that day
            setSelectedDay(clickedDate);
            setSelectedTimelineIndex(index);
            setSelectedMonth(null);
            setCurrentContext(clickedDate);
            zoomToLevel("daily", clickedDate);
          }
          break;
        }

        case "session": {
          if (
            selectedDay &&
            clickedDate.toDateString() === selectedDay.toDateString()
          ) {
            // Same day → Zoom out to yearly

            const yearStart = new Date(clickedDate.getFullYear(), 0, 1);
            const clickedMonth = clickedDate.getMonth();

            setSelectedDay(null);
            setSelectedTimelineIndex(null);
            setSelectedMonth(clickedMonth);
            setCurrentContext(yearStart);
            zoomToLevel("yearly", yearStart);
          } else {
            // Different day → Zoom to daily
            setSelectedDay(clickedDate);
            setSelectedTimelineIndex(index);
            setSelectedMonth(null);
            setCurrentContext(clickedDate);
            zoomToLevel("daily", clickedDate);
          }
          break;
        }
      }
    },
    [aggregationLevel, selectedDay, selectedMonth, zoomToLevel, dateRange]
  );

  // Keep viewport sync
  useEffect(() => {
    if (!viewport || !data || data.length === 0) return;

    // Get the center point of current viewport
    const viewportCenter = new Date(
      (viewport.start.getTime() + viewport.end.getTime()) / 2
    );

    // Only update if date changed (not time within same day)
    const currentDateStr = currentContext.toDateString();
    const viewportDateStr = viewportCenter.toDateString();

    if (currentDateStr !== viewportDateStr) {
      setCurrentContext(viewportCenter);
    }
  }, [viewport, data, currentContext]);

  useEffect(() => {
    if (aggregationLevel !== "monthly" || !viewport) return;
    if (isTimelineSyncingRef.current) return; // Skip during timeline sync

    const viewportCenter = new Date(
      (viewport.start.getTime() + viewport.end.getTime()) / 2
    );

    if (currentContext.toDateString() !== viewportCenter.toDateString()) {
      setCurrentContext(new Date(viewportCenter.getTime()));
    }
  }, [viewport, aggregationLevel, currentContext]);

  const handleTimelineScroll = useCallback(
    (start: Date, end: Date) => {
      // Calculate timeline's center date
      const timelineCenter = new Date((start.getTime() + end.getTime()) / 2);

      // Get current viewport duration (MAINTAIN THIS)
      const viewportDuration = viewport
        ? viewport.end.getTime() - viewport.start.getTime()
        : 0;
      const viewportDays = Math.round(viewportDuration / (1000 * 60 * 60 * 24));

      // Calculate new viewport bounds centered on timeline's center
      // Width stays the same, only center position changes
      const halfDuration = viewportDuration / 2;
      const newStart = new Date(timelineCenter.getTime() - halfDuration);
      const newEnd = new Date(timelineCenter.getTime() + halfDuration);

      setViewport({
        start: newStart,
        end: newEnd,
      });
    },
    [viewport] // Dynamic dependency - tracks current viewport state
  );

  // -------------------- Fullscreen handlers --------------------
  const handleLockLandscape = useCallback(async () => {
    try {
      if (ScreenOrientation?.lock) {
        await ScreenOrientation.lock({ orientation: "landscape" });
      }
    } catch (e) {
      console.warn("Failed to lock to landscape:", e);
    }
  }, []);

  // Handler to unlock orientation (return to portrait/auto)
  const handleUnlockOrientation = useCallback(async () => {
    try {
      if (ScreenOrientation?.unlock) {
        await ScreenOrientation.unlock();
      }
    } catch (e) {
      console.warn("Failed to unlock orientation:", e);
    }
  }, []);

  // Handler for opening fullscreen modal
  const openFullscreen = useCallback(async () => {
    setIsFullscreen(true);
    await handleLockLandscape();
  }, [handleLockLandscape]);

  // Handler for closing fullscreen modal
  const closeFullscreen = useCallback(async () => {
    await handleUnlockOrientation();
    setIsFullscreen(false);
  }, [handleUnlockOrientation]);

  // Handler for page leave (swipe back, hardware back, etc.)
  const handlePageLeave = useCallback(async () => {
    await handleUnlockOrientation();
    if (isFullscreen) {
      setIsFullscreen(false);
    }
  }, [handleUnlockOrientation, isFullscreen]);

  // ==================== LIFECYCLE HOOKS ====================

  // Handle page navigation away (swipe back, hardware back)
  useIonViewWillLeave(() => {
    handlePageLeave();
  });

  // Handle orientation changes when modal state changes
  useEffect(() => {
    const handleOrientationChange = async () => {
      if (isFullscreen) {
        await handleLockLandscape();
      } else {
        await handleUnlockOrientation();
      }
    };

    handleOrientationChange();

    // Cleanup on unmount
    return () => {
      handleUnlockOrientation();
    };
  }, [isFullscreen, handleLockLandscape, handleUnlockOrientation]);

  // ==================== NEW: PREVENT BACK NAVIGATION ====================

  // Prevent back navigation when modal is open
  useEffect(() => {
    if (isFullscreen) {

      const setupBackPrevention = async () => {
        // 2. Handle ionic back button event
        const handleIonBack = (ev: any) => {
          ev.detail.register(100, () => {
            closeFullscreen(); // Close modal instead of going back
          });
        };
        document.addEventListener('ionBackButton', handleIonBack);
        return () => {
          document.removeEventListener('ionBackButton', handleIonBack);
        };
      };

      const cleanup = setupBackPrevention();

      return () => {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      };
    } else {
      return;
    }
  }, [isFullscreen, closeFullscreen]);


  //therapy session segmentation helper
  const calculateSessionSegments = useCallback(
    (
      sessions: TherapySession[],
      visibleData: RenderableDataPoint[],
      chartWidth: number
    ): SessionSegment[] => {
      if (sessions.length === 0) return [];

      const sessionCount = sessions.length;
      const totalCycles = visibleData.length;

      // IMPROVED: Responsive gap width based on screen size AND session count
      let baseGapWidth: number;
      if (chartWidth < 600) {
        // Mobile
        baseGapWidth = 30;
      } else {
        // Desktop
        baseGapWidth = 100;
      }

      // Further reduce gaps if many sessions
      const gapWidth =
        sessionCount <= 3
          ? baseGapWidth
          : sessionCount <= 5
            ? Math.max(20, baseGapWidth * 0.6) // 60% of base for 4-5 sessions
            : Math.max(15, baseGapWidth * 0.4); // 40% of base for 6+ sessions

      // Dynamic cycle padding based on session count (tighter when crowded)
      const cyclePadding = sessionCount > 4 ? 0.2 : 0.3;

      // Calculate total gap width
      const totalGapWidth = gapWidth * (sessionCount + 1);
      const availableWidth = chartWidth - totalGapWidth;

      // First pass: Calculate proportional widths
      const segments: SessionSegment[] = [];
      let currentX = gapWidth;

      sessions.forEach((session) => {
        // Find cycles for this session
        const cycleIndices = visibleData
          .map((d, i) =>
            !isAggregated(d) && (d as TherapyDataPoint).refId === session.refId
              ? i
              : -1
          )
          .filter((i) => i !== -1);

        const cycleCount = cycleIndices.length;

        // Width proportional to cycle count
        const segmentWidth = (cycleCount / totalCycles) * availableWidth;

        segments.push({
          session,
          cycleIndices,
          startX: currentX,
          endX: currentX + segmentWidth,
          width: segmentWidth,
          gapAfter: gapWidth,
        });

        currentX += segmentWidth + gapWidth;
      });

      // Calculate if bars would be too small
      const minBarWidth = mergedConfig.features.minBarWidth || 8;
      let needsAdjustment = false;

      for (const seg of segments) {
        const cyclesInSegment = seg.cycleIndices.length;
        const estimatedBarWidth =
          seg.width / cyclesInSegment / (1 + cyclePadding);

        if (estimatedBarWidth < minBarWidth) {
          needsAdjustment = true;
          break;
        }
      }

      // Second pass: Adjust if bars would be too small
      if (needsAdjustment) {
        // Calculate minimum required width for each segment
        let totalRequiredWidth = 0;

        segments.forEach((seg) => {
          const cyclesInSegment = seg.cycleIndices.length;
          const minSegmentWidth =
            cyclesInSegment * minBarWidth * (1 + cyclePadding);
          seg.width = minSegmentWidth;
          totalRequiredWidth += minSegmentWidth;
        });

        // Check if total required width exceeds available space
        const totalWidthWithGaps = totalRequiredWidth + totalGapWidth;

        if (totalWidthWithGaps > chartWidth) {
          // IMPROVED: Try reducing gaps first before scaling segments
          const excessWidth = totalWidthWithGaps - chartWidth;
          const maxGapReduction = gapWidth * 0.7; // Can reduce gaps up to 70%
          const gapReduction = Math.min(
            maxGapReduction,
            excessWidth / (sessionCount + 1)
          );
          const newGapWidth = Math.max(10, gapWidth - gapReduction); // Min 10px gap
          const newTotalGapWidth = newGapWidth * (sessionCount + 1);
          const newAvailableWidth = chartWidth - newTotalGapWidth;

          // Check if reduced gaps are enough
          if (totalRequiredWidth <= newAvailableWidth) {
            // Gaps reduction solved the problem
            currentX = newGapWidth;

            segments.forEach((seg) => {
              seg.startX = currentX;
              seg.endX = currentX + seg.width;
              seg.gapAfter = newGapWidth;
              currentX += seg.width + newGapWidth;
            });
          } else {
            // Still need to scale segments after gap reduction
            const scaleFactor = newAvailableWidth / totalRequiredWidth;
            currentX = newGapWidth;

            segments.forEach((seg) => {
              const scaledWidth = seg.width * scaleFactor;

              seg.startX = currentX;
              seg.endX = currentX + scaledWidth;
              seg.width = scaledWidth;
              seg.gapAfter = newGapWidth;

              currentX += scaledWidth + newGapWidth;
            });
          }
        } else {
          // Fits with minimum widths, recalculate positions
          currentX = gapWidth;

          segments.forEach((seg) => {
            seg.startX = currentX;
            seg.endX = currentX + seg.width;
            currentX += seg.width + gapWidth;
          });
        }
      } else {
        // Third pass: Check for overflow and scale if needed
        const totalWidth = currentX; // Includes final gap

        if (totalWidth > chartWidth) {
          // IMPROVED: Try reducing gaps first
          const excessWidth = totalWidth - chartWidth;
          const maxGapReduction = gapWidth * 0.5; // Can reduce up to 50%
          const gapReduction = Math.min(
            maxGapReduction,
            excessWidth / (sessionCount + 1)
          );
          const newGapWidth = Math.max(10, gapWidth - gapReduction);

          currentX = newGapWidth;

          segments.forEach((seg) => {
            seg.startX = currentX;
            seg.endX = currentX + seg.width;
            seg.gapAfter = newGapWidth;
            currentX += seg.width + newGapWidth;
          });

          // If still overflowing after gap reduction, scale everything
          if (currentX > chartWidth) {
            const scaleFactor =
              (chartWidth - newGapWidth) / (currentX - newGapWidth);
            currentX = newGapWidth * scaleFactor;

            segments.forEach((seg) => {
              const scaledWidth = seg.width * scaleFactor;
              const scaledGap = newGapWidth * scaleFactor;

              seg.startX = currentX;
              seg.endX = currentX + scaledWidth;
              seg.width = scaledWidth;
              seg.gapAfter = scaledGap;

              currentX += scaledWidth + scaledGap;
            });
          }
        }
      }

      return segments;
    },
    [mergedConfig.features.minBarWidth]
  );

  // zoom helpers:
  // Helper: Find closest data point to a screen coordinate
  const findClosestDataPoint = useCallback(
    (clientX: number, chartWidth: number): Date => {
      if (!viewport) {
        // console.log("⚠️ No viewport - returning current date");
        return new Date();
      }

      // FIXED: Check if visibleData is empty
      if (data.length === 0 || visibleData.length === 0) {
        // console.log("⚠️ No data available - using viewport center");
        const centerTime =
          viewport.start.getTime() +
          (viewport.end.getTime() - viewport.start.getTime()) / 2;
        return new Date(centerTime);
      }

      const x = clientX;

      // DAILY VIEW: Segmented by sessions
      if (aggregationLevel === "daily") {
        const sessionSegments = calculateSessionSegments(
          therapySessions.filter((s) => {
            const sessionTime = new Date(s.startTime).getTime();
            return (
              sessionTime >= viewport.start.getTime() &&
              sessionTime <= viewport.end.getTime()
            );
          }),
          visibleData,
          chartWidth
        );

        // FIX: Check if segments exist before accessing
        if (sessionSegments.length === 0) {
          // Return center of viewport as fallback
          const centerTime =
            viewport.start.getTime() +
            (viewport.end.getTime() - viewport.start.getTime()) / 2;
          return new Date(centerTime);
        }

        // Find the segment the touch is in, or the nearest segment edge when
        // the touch falls in a gap. (Nearest *centre* would pick a neighbouring
        // short session when touching the edge bars of a long one.)
        let closestSegment = sessionSegments[0];
        let minSegmentDistance = Infinity;

        for (const segment of sessionSegments) {
          const distance =
            x < segment.startX
              ? segment.startX - x
              : x > segment.endX
                ? x - segment.endX
                : 0;

          if (distance < minSegmentDistance) {
            minSegmentDistance = distance;
            closestSegment = segment;
          }
        }

        // Within the closest segment, find the closest data point
        if (closestSegment && closestSegment.cycleIndices.length > 0) {
          const segmentScale = scaleLinear({
            domain: [0, Math.max(1, closestSegment.cycleIndices.length - 1)],
            range: [closestSegment.startX, closestSegment.endX],
          });

          let closestIndexInSegment = 0;
          let minDistance = Math.abs(segmentScale(0) - x);

          for (let i = 1; i < closestSegment.cycleIndices.length; i++) {
            const renderedX = segmentScale(i);
            const distance = Math.abs(renderedX - x);

            if (distance < minDistance) {
              minDistance = distance;
              closestIndexInSegment = i;
            }
          }

          const dataIndex = closestSegment.cycleIndices[closestIndexInSegment];
          const closestPoint = visibleData[dataIndex];

          return new Date(closestPoint.timestamp);
        }
        const centerTime =
          viewport.start.getTime() +
          (viewport.end.getTime() - viewport.start.getTime()) / 2;
        return new Date(centerTime);
      }

      // YEARLY/MONTHLY: Time-based scale
      // FIXED: Check again in case visibleData became empty
      if (visibleData.length === 0) {
        const centerTime =
          viewport.start.getTime() +
          (viewport.end.getTime() - viewport.start.getTime()) / 2;
        return new Date(centerTime);
      }

      const xScale = scaleTime({
        domain: [viewport.start, viewport.end],
        range: [0, chartWidth],
      });

      let closestPoint = visibleData[0];
      let minDistance = Math.abs(
        xScale(new Date(visibleData[0].timestamp)) - x
      );

      for (let i = 1; i < visibleData.length; i++) {
        const renderedX = xScale(new Date(visibleData[i].timestamp));
        const distance = Math.abs(renderedX - x);

        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = visibleData[i];
        }
      }

      return new Date(closestPoint.timestamp);
    },
    [
      viewport,
      mergedConfig.layout.margins,
      data,
      aggregationLevel,
      therapySessions,
      visibleData,
      calculateSessionSegments,
    ]
  );

  // Convert a pointer clientX to chart X (same space as the drawn bars).
  // The overlay rect already sits inside the margin-translated Group, so no
  // margin subtraction; scale handles the svg being CSS-scaled (maxWidth: 100%).
  function normalizeTouchX(clientX: number, rect: DOMRect): number {
    if (rect.width === 0) return 0;
    return (clientX - rect.left) * (chartWidthRef.current / rect.width);
  }

  // Handle wheel zoom from timeline
  // Find your existing handleTimelineWheelZoom and REPLACE it with this:

  // Find your existing handleTimelineWheelZoom and REPLACE it with this:

  const handleTimelineWheelZoom = useCallback(
    (direction: "in" | "out") => {
      if (direction === "in") {
        // Zoom IN
        if (aggregationLevel === "yearly") {
          // Find a month with data or use current context
          const targetMonth = currentContext;
          setCurrentContext(targetMonth);
          zoomToLevel("monthly", targetMonth);
        } else if (aggregationLevel === "monthly") {
          // Find a day with data in viewport or use selected day
          const targetDay = selectedDay || currentContext;
          setSelectedDay(targetDay);
          setCurrentContext(targetDay);
          zoomToLevel("daily", targetDay);
        } else if (aggregationLevel === "daily") {
          // Zoom to session level
          zoomToLevel("session", currentContext);
        }
      } else {
        // Zoom OUT
        if (aggregationLevel === "session") {
          // Session → Daily
          // FIXED Issue #5: Use selectedDay or find closest date with data
          let targetDay = selectedDay || currentContext;

          // If current date has no data, find nearest date with data
          if (
            !monthlyData.some(
              (d) =>
                new Date(d.timestamp).toDateString() ===
                targetDay.toDateString()
            )
          ) {
            // Find closest date with data
            const currentTime = targetDay.getTime();
            let closestDate = null;
            let minDiff = Infinity;

            monthlyData.forEach((d) => {
              const diff = Math.abs(d.timestamp - currentTime);
              if (diff < minDiff) {
                minDiff = diff;
                closestDate = new Date(d.timestamp);
              }
            });

            if (closestDate) {
              targetDay = closestDate;
            }
          }

          setCurrentContext(targetDay);
          setSelectedDay(targetDay);
          zoomToLevel("daily", targetDay);
        } else if (aggregationLevel === "daily") {
          // Daily → Monthly
          // FIXED Issue #5: Clear selection and zoom to month
          setSelectedDay(null);
          setSelectedTimelineIndex(null);

          const monthStart = new Date(
            currentContext.getFullYear(),
            currentContext.getMonth(),
            1
          );
          setCurrentContext(monthStart);
          zoomToLevel("monthly", monthStart);
        } else if (aggregationLevel === "monthly") {
          // Monthly → Yearly
          setSelectedDay(null);
          setSelectedTimelineIndex(null);

          const yearStart = new Date(currentContext.getFullYear(), 0, 1);
          setCurrentContext(yearStart);
          zoomToLevel("yearly", yearStart);
        }
      }
    },
    [aggregationLevel, currentContext, selectedDay, monthlyData, zoomToLevel]
  );

  // Helper: Find next session after current one
  const findNextSession = useCallback(
    (
      currentRefId: number
    ): { session: TherapySession; timestamp: Date } | null => {
      if (!viewport) return null;

      const currentSession = therapySessions.find(
        (s) => s.refId === currentRefId
      );
      if (!currentSession) return null;

      const currentDay = startOfDay(new Date(currentSession.startTime));

      // Find sessions in current day
      const sessionsInDay = therapySessions
        .filter((s) => {
          const sessionDay = startOfDay(new Date(s.startTime));
          return sessionDay.getTime() === currentDay.getTime();
        })
        .sort((a, b) => a.startTime - b.startTime);

      // Find next session in same day
      const currentIndex = sessionsInDay.findIndex(
        (s) => s.refId === currentRefId
      );
      if (currentIndex !== -1 && currentIndex < sessionsInDay.length - 1) {
        const nextSession = sessionsInDay[currentIndex + 1];
        return {
          session: nextSession,
          timestamp: new Date(nextSession.startTime),
        };
      }

      // No more sessions in current day
      // Check ONLY the immediate next day - don't search multiple days
      const nextDay = new Date(currentDay);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStart = startOfDay(nextDay);
      const nextDayEnd = endOfDay(nextDay);

      const sessionsInNextDay = therapySessions
        .filter((s) => {
          const sessionTime = new Date(s.startTime).getTime();
          return (
            sessionTime >= nextDayStart.getTime() &&
            sessionTime <= nextDayEnd.getTime()
          );
        })
        .sort((a, b) => a.startTime - b.startTime);

      if (sessionsInNextDay.length > 0) {
        const firstSession = sessionsInNextDay[0];
        return {
          session: firstSession,
          timestamp: new Date(firstSession.startTime),
        };
      }

      // Next day has no sessions - return null to trigger zoom out
      return null;
    },
    [therapySessions, viewport]
  );

  // Helper: Find previous session before current one
  const findPreviousSession = useCallback(
    (
      currentRefId: number
    ): { session: TherapySession; timestamp: Date } | null => {
      if (!viewport) return null;

      const currentSession = therapySessions.find(
        (s) => s.refId === currentRefId
      );
      if (!currentSession) return null;

      const currentDay = startOfDay(new Date(currentSession.startTime));

      // Find sessions in current day
      const sessionsInDay = therapySessions
        .filter((s) => {
          const sessionDay = startOfDay(new Date(s.startTime));
          return sessionDay.getTime() === currentDay.getTime();
        })
        .sort((a, b) => a.startTime - b.startTime);

      // Find previous session in same day
      const currentIndex = sessionsInDay.findIndex(
        (s) => s.refId === currentRefId
      );
      if (currentIndex > 0) {
        const prevSession = sessionsInDay[currentIndex - 1];
        return {
          session: prevSession,
          timestamp: new Date(prevSession.startTime),
        };
      }

      // No previous sessions in current day
      // Check ONLY the immediate previous day - don't search multiple days
      const prevDay = new Date(currentDay);
      prevDay.setDate(prevDay.getDate() - 1);
      const prevDayStart = startOfDay(prevDay);
      const prevDayEnd = endOfDay(prevDay);

      const sessionsInPrevDay = therapySessions
        .filter((s) => {
          const sessionTime = new Date(s.startTime).getTime();
          return (
            sessionTime >= prevDayStart.getTime() &&
            sessionTime <= prevDayEnd.getTime()
          );
        })
        .sort((a, b) => a.startTime - b.startTime);

      if (sessionsInPrevDay.length > 0) {
        const lastSession = sessionsInPrevDay[sessionsInPrevDay.length - 1];
        return {
          session: lastSession,
          timestamp: new Date(lastSession.startTime),
        };
      }

      // Previous day has no sessions - return null to trigger zoom out
      return null;
    },
    [therapySessions, viewport]
  );

  const handleDragMove = useCallback(
    (clientX: number, passedChartWidth?: number) => {
      if (!isDragging || !dragStartRef.current || !viewport) return;

      const deltaX = clientX - dragStartRef.current.x;
      const dragThreshold = 50;

      if (Math.abs(deltaX) < dragThreshold) return;

      const chartWidth = passedChartWidth ?? chartWidthRef.current;
      if (chartWidth === 0) return;

      // ========== SESSION VIEW: Navigate between sessions ==========
      if (aggregationLevel === "session" && currentSessionRefId !== null) {
        if (deltaX < 0) {
          // Swiped left - go to next session
          const nextSession = findNextSession(currentSessionRefId);

          if (nextSession) {
            zoomToLevel("session", nextSession.timestamp);

            // FIXED: nextSession.timestamp is already a Date object
            const sessionDate = new Date(nextSession.timestamp);
            // Force state update by creating a completely new Date
            setCurrentContext(new Date(sessionDate.getTime()));

            // ADDED: Also update selectedDay to match
            const dayDate = new Date(sessionDate);
            dayDate.setHours(0, 0, 0, 0);
            setSelectedDay(new Date(dayDate.getTime()));
            dragStartRef.current = { x: clientX, viewport: { ...viewport } };
            return;
          } else {
            // No more sessions - zoom out to NEXT day
            const currentSession = therapySessions.find(
              (s) => s.refId === currentSessionRefId
            );
            if (currentSession) {
              const currentDay = startOfDay(new Date(currentSession.startTime));
              const nextDay = new Date(currentDay);
              nextDay.setDate(nextDay.getDate() + 1);

              setAggregationLevel("daily");
              setActiveData(data);
              setViewport({
                start: startOfDay(nextDay),
                end: endOfDay(nextDay),
              });
              setCurrentSessionRefId(null);

              setCurrentContext(new Date(nextDay.getTime()));

              if (selectedDay) {
                setSelectedDay(new Date(nextDay.getTime()));
              }

              const nextDayStart = startOfDay(nextDay).getTime();
              const nextDayEnd = endOfDay(nextDay).getTime();

              let startIdx = -1;
              let endIdx = -1;

              for (let i = 0; i < data.length; i++) {
                if (
                  data[i].timestamp >= nextDayStart &&
                  data[i].timestamp <= nextDayEnd
                ) {
                  if (startIdx === -1) startIdx = i;
                  endIdx = i;
                }
              }
            }
            setIsDragging(false);
            dragStartRef.current = null;
            return;
          }
        } else {
          // Swiped right - go to previous session
          const prevSession = findPreviousSession(currentSessionRefId);

          if (prevSession) {
            zoomToLevel("session", prevSession.timestamp);

            // FIXED: prevSession.timestamp is already a Date object
            const sessionDate = new Date(prevSession.timestamp);
            // Force state update by creating a completely new Date
            setCurrentContext(new Date(sessionDate.getTime()));

            // ADDED: Also update selectedDay to match
            const dayDate = new Date(sessionDate);
            dayDate.setHours(0, 0, 0, 0);
            setSelectedDay(new Date(dayDate.getTime()));
            dragStartRef.current = { x: clientX, viewport: { ...viewport } };
            return;
          } else {
            // No previous sessions - zoom out to PREVIOUS day
            const currentSession = therapySessions.find(
              (s) => s.refId === currentSessionRefId
            );
            if (currentSession) {
              const currentDay = startOfDay(new Date(currentSession.startTime));
              const prevDay = new Date(currentDay);
              prevDay.setDate(prevDay.getDate() - 1);

              setAggregationLevel("daily");
              setActiveData(data);
              setViewport({
                start: startOfDay(prevDay),
                end: endOfDay(prevDay),
              });
              setCurrentSessionRefId(null);

              setCurrentContext(new Date(prevDay.getTime()));

              if (selectedDay) {
                setSelectedDay(new Date(prevDay.getTime()));
              }

              const prevDayStart = startOfDay(prevDay).getTime();
              const prevDayEnd = endOfDay(prevDay).getTime();

              let startIdx = -1;
              let endIdx = -1;

              for (let i = 0; i < data.length; i++) {
                if (
                  data[i].timestamp >= prevDayStart &&
                  data[i].timestamp <= prevDayEnd
                ) {
                  if (startIdx === -1) startIdx = i;
                  endIdx = i;
                }
              }
            }
            setIsDragging(false);
            dragStartRef.current = null;
            return;
          }
        }
      }

      // ========== DAILY/MONTHLY/YEARLY: Original snap-to-period logic ==========
      if (!fullCalendarBounds) return;

      const { start, end } = dragStartRef.current.viewport;

      const viewportDuration = end.getTime() - start.getTime();
      const pixelToTime = viewportDuration / chartWidth;
      const timeDelta = deltaX * pixelToTime;

      let snapThreshold: number;
      let periodsToMove = 0;

      if (aggregationLevel === "yearly") {
        const monthDuration = 30 * 24 * 60 * 60 * 1000;
        snapThreshold = monthDuration * 0.3;
        periodsToMove = Math.round(timeDelta / monthDuration);
      } else if (aggregationLevel === "monthly") {
        const dayDuration = 24 * 60 * 60 * 1000;
        snapThreshold = dayDuration * 0.2;
        periodsToMove = Math.round(timeDelta / dayDuration);
      } else {
        const hourDuration = 60 * 60 * 1000;
        const viewportHours = viewportDuration / hourDuration;

        if (viewportHours >= 20) {
          const dayDuration = 24 * 60 * 60 * 1000;
          snapThreshold = dayDuration * 0.15;
          periodsToMove = Math.round(timeDelta / dayDuration);
        } else {
          snapThreshold = viewportDuration * 0.25;
          periodsToMove = Math.round(timeDelta / (viewportDuration * 0.5));
        }
      }

      if (Math.abs(timeDelta) < snapThreshold) return;

      if (periodsToMove === 0) {
        periodsToMove = timeDelta > 0 ? 1 : -1;
      }

      let newStart: Date;
      let newEnd: Date;

      if (aggregationLevel === "yearly") {
        newStart = addMonths(start, -periodsToMove);
        newEnd = addMonths(end, -periodsToMove);
      } else {
        const moveAmount =
          aggregationLevel === "daily" && viewportDuration < 20 * 60 * 60 * 1000
            ? -periodsToMove * (viewportDuration * 0.5)
            : -periodsToMove * 24 * 60 * 60 * 1000;

        newStart = new Date(start.getTime() + moveAmount);
        newEnd = new Date(end.getTime() + moveAmount);
      }

      if (newStart.getTime() < fullCalendarBounds.start.getTime()) {
        const diff = end.getTime() - start.getTime();
        newStart = new Date(fullCalendarBounds.start);
        newEnd = new Date(fullCalendarBounds.start.getTime() + diff);
      }

      if (newEnd.getTime() > fullCalendarBounds.end.getTime()) {
        const diff = end.getTime() - start.getTime();
        newEnd = new Date(fullCalendarBounds.end);
        newStart = new Date(fullCalendarBounds.end.getTime() - diff);
      }

      setViewport({ start: newStart, end: newEnd });

      const newViewportCenter = new Date(
        (newStart.getTime() + newEnd.getTime()) / 2
      );

      if (currentContext.toDateString() !== newViewportCenter.toDateString()) {
        setCurrentContext(new Date(newViewportCenter.getTime()));

        if (aggregationLevel === "daily" && selectedDay) {
          if (selectedDay.toDateString() !== newViewportCenter.toDateString()) {
            setSelectedDay(new Date(newViewportCenter.getTime()));
          }
        }
      }

      dragStartRef.current = {
        x: clientX,
        viewport: { start: newStart, end: newEnd },
      };
    },
    [
      isDragging,
      viewport,
      fullCalendarBounds,
      aggregationLevel,
      currentSessionRefId,
      findNextSession,
      findPreviousSession,
      zoomToLevel,
      therapySessions,
      data,
      setAggregationLevel,
      setActiveData,
      setViewport,
      setCurrentSessionRefId,
      currentContext,
      setCurrentContext,
      selectedDay,
      setSelectedDay,
    ]
  );

  const handlePinchStart = useCallback(
    (touch1: Touch, touch2: Touch) => {
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const rect = mainGraphOverlayRef.current?.getBoundingClientRect();
      if (!rect) return;

      const anchorClientX = (touch1.clientX + touch2.clientX) / 2;
      const anchor = normalizeTouchX(anchorClientX, rect);

      zoomStateRef.current = {
        initialDistance: distance,
        initialLevel: aggregationLevel,
        accumulatedDelta: 0,
        anchorX: anchor,
        hasZoomedThisGesture: false, // Reset at start
      };
    },
    [aggregationLevel]
  );

  const handlePinchMove = useCallback(
    (touch1: Touch, touch2: Touch) => {
      if (!zoomStateRef.current || !mergedConfig.zoom.enabled || !viewport)
        return;

      const currentDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const deltaDistance =
        currentDistance - zoomStateRef.current.initialDistance;
      const distanceRatio =
        Math.abs(deltaDistance) / zoomStateRef.current.initialDistance;

      if (distanceRatio >= mergedConfig.zoom.discrete.pinchThreshold) {
        if (zoomStateRef.current.hasZoomedThisGesture) {
          // console.log("🔒 Zoom locked - already zoomed this gesture");
          return;
        }

        const isZoomingIn = deltaDistance > 0;
        const levels = mergedConfig.zoom.discrete.levels;
        const currentIndex = levels.indexOf(aggregationLevel);

        let targetLevel: AggregationLevel | null = null;

        if (isZoomingIn && currentIndex < levels.length - 1) {
          targetLevel = levels[currentIndex + 1];
        } else if (!isZoomingIn && currentIndex > 0) {
          if (aggregationLevel === "daily") {
            const rangeDays = Math.ceil(
              (new Date(dateRange.endDate).getTime() -
                new Date(dateRange.startDate).getTime()) /
              (1000 * 60 * 60 * 24)
            );

            if (rangeDays < 32) {
              zoomStateRef.current = {
                initialDistance: currentDistance,
                initialLevel: aggregationLevel,
                accumulatedDelta: 0,
                anchorX: zoomStateRef.current.anchorX,
                hasZoomedThisGesture: false,
              };
              return;
            }
          }

          targetLevel = levels[currentIndex - 1];
        }

        if (targetLevel) {
          const centerX = zoomStateRef.current.anchorX;

          // anchorX is in chart units (normalizeTouchX), so use the drawn width
          const chartWidth = chartWidthRef.current;
          const centerTimestamp = findClosestDataPoint(centerX, chartWidth);

          zoomToLevel(targetLevel, centerTimestamp);

          // ADDED: Update currentContext for timeline sync
          const zoomDate = new Date(centerTimestamp);
          setCurrentContext(zoomDate);

          if (aggregationLevel === "daily" && targetLevel === "monthly") {
            setSelectedDay(null);
            setSelectedTimelineIndex(null);
          }

          if (aggregationLevel === "monthly" && targetLevel === "yearly") {
            setSelectedDay(null);
            setSelectedMonth(null);
            setSelectedTimelineIndex(null);
          }

          if (aggregationLevel === "monthly" && targetLevel === "daily") {
            setSelectedDay(zoomDate);
          }

          zoomStateRef.current = {
            initialDistance: currentDistance,
            initialLevel: targetLevel,
            accumulatedDelta: 0,
            anchorX: zoomStateRef.current.anchorX,
            hasZoomedThisGesture: true,
          };
        }
      }
    },
    [
      aggregationLevel,
      mergedConfig,
      findClosestDataPoint,
      zoomToLevel,
      viewport,
      dateRange,
      setCurrentContext,
      setSelectedDay,
      setSelectedMonth,
      setSelectedTimelineIndex,
    ]
  );

  const handlePinchEnd = useCallback(() => {
    zoomStateRef.current = null;
  }, []);

  // Mouse wheel zoom handler
  // Mouse wheel zoom handler
  const handleWheelZoom = useCallback(
    (deltaY: number, clientX: number, overlayRect: DOMRect) => {
      if (!mergedConfig.zoom.enabled || !viewport) return;

      wheelDeltaRef.current += deltaY;
      const threshold = mergedConfig.zoom.discrete.mouseWheelThreshold;

      if (Math.abs(wheelDeltaRef.current) >= threshold) {
        const isZoomingIn = wheelDeltaRef.current < 0;
        const levels = mergedConfig.zoom.discrete.levels;
        const currentIndex = levels.indexOf(aggregationLevel);

        let targetLevel: AggregationLevel | null = null;

        if (isZoomingIn && currentIndex < levels.length - 1) {
          targetLevel = levels[currentIndex + 1];
        } else if (!isZoomingIn && currentIndex > 0) {
          if (aggregationLevel === "daily") {
            const rangeDays = Math.ceil(
              (new Date(dateRange.endDate).getTime() -
                new Date(dateRange.startDate).getTime()) /
              (1000 * 60 * 60 * 24)
            );
            if (rangeDays < 32) {
              wheelDeltaRef.current = 0;
              return;
            }
          }
          targetLevel = levels[currentIndex - 1];
        }

        if (targetLevel) {
          const chartWidth = chartWidthRef.current;

          // Convert screen X → chart X
          const chartX = normalizeTouchX(clientX, overlayRect);
          const centerTimestamp = findClosestDataPoint(chartX, chartWidth);

          zoomToLevel(targetLevel, centerTimestamp);

          // ADDED: Update currentContext for timeline sync
          const zoomDate = new Date(centerTimestamp);
          setCurrentContext(zoomDate);

          if (aggregationLevel === "daily" && targetLevel === "monthly") {
            setSelectedDay(null);
            setSelectedTimelineIndex(null);
          }

          if (aggregationLevel === "monthly" && targetLevel === "yearly") {
            setSelectedDay(null);
            setSelectedMonth(null);
            setSelectedTimelineIndex(null);
          }

          if (aggregationLevel === "monthly" && targetLevel === "daily") {
            setSelectedDay(zoomDate);
          }
        }

        wheelDeltaRef.current = 0;
      }
    },
    [
      aggregationLevel,
      mergedConfig,
      findClosestDataPoint,
      zoomToLevel,
      viewport,
      dateRange,
      setCurrentContext,
      setSelectedDay,
      setSelectedMonth,
      setSelectedTimelineIndex,
    ]
  );

  /**
   * Determines if a peak pressure value is out of safe range and returns appropriate color
   *
   * @param peakValue - The peak inspiratory pressure value
   * @param setInspValue - The set inspiratory pressure value (baseline)
   * @param config - Configuration object with thresholds and colors
   * @returns Color string (normal or danger)
   */
  const getPeakPressureColor = (
    peakValue: number,
    setInspValue: number,
    config: {
      thresholds: {
        enabled: boolean;
        setThreshold: number;
        tolerance: number;
        highlightMode: "both" | "high" | "low" | "none";
      };
      colors: {
        normal: string;
        danger: string;
      };
    }
  ): string => {
    // If thresholds disabled or mode is "none", always use normal color
    if (
      !config.thresholds.enabled ||
      config.thresholds.highlightMode === "none"
    ) {
      return config.colors.normal;
    }

    const { setThreshold, tolerance, highlightMode } = config.thresholds;

    // Calculate safe range
    const maxSafe = setInspValue + setThreshold + tolerance;
    const minSafe = setInspValue - setThreshold - tolerance;

    // Check if out of range based on highlight mode
    const isTooHigh = peakValue > maxSafe;
    const isTooLow = peakValue < minSafe;

    let isDangerous = false;

    switch (highlightMode) {
      case "both":
        isDangerous = isTooHigh || isTooLow;
        break;
      case "high":
        isDangerous = isTooHigh;
        break;
      case "low":
        isDangerous = isTooLow;
        break;
    }

    return isDangerous ? config.colors.danger : config.colors.normal;
  };

  const thresholdConfig = {
    thresholds: mergedConfig.peakPressureThresholds,
    colors: {
      normal: mergedConfig.colors.peakInsp,
      danger: mergedConfig.colors.peakInspDanger,
    },
  };

  const isMobilePhone = useMemo(() => isPlatform("mobile"), []);
  const getVitalsRenderData = (
    vitalsData: VitalsData | null,
    selectedMetric: MeasurementType,
    aggregationLevel: AggregationLevel,
    visibleData: RenderableDataPoint[],
    viewport: { start: Date; end: Date } | null // ← NEW PARAMETER
  ): RenderableDataPoint[] => {
    if (!vitalsData || !isVitalsParameter(selectedMetric)) {
      // console.log("❌ Early return: no vitals data or not vitals parameter");
      return [];
    }

    if (!viewport) return [];

    // ============================================
    // USE FULL DATE RANGE (not visible viewport)
    // ============================================
    // Use viewport instead of fullDateRange
    const minTime = viewport.start.getTime();
    const maxTime = viewport.end.getTime();

    if (aggregationLevel === "yearly" || aggregationLevel === "monthly") {
      // ============================================
      // AGGREGATED VIEWS: Use actual vitals aggregates
      // ============================================

      if (aggregationLevel === "yearly") {
        // Handle monthly data
        const filtered = vitalsData.monthly
          .filter((item) => {
            const timestamp = parseVitalsTimestamp(
              item.month,
              aggregationLevel
            );
            const vitalData = item[
              selectedMetric as keyof MonthlyVitals
            ] as VitalsDataPoint;

            // Filter 1: Time range
            if (timestamp < minTime || timestamp > maxTime) return false;

            // Filter 2: Has valid data
            return vitalData.min !== null && vitalData.max !== null;
          })
          .map((item) => {
            const timestamp = parseVitalsTimestamp(
              item.month,
              aggregationLevel
            );
            const vitalData = item[
              selectedMetric as keyof MonthlyVitals
            ] as VitalsDataPoint;
            const mean =
              vitalData.mean !== null
                ? vitalData.mean
                : (vitalData.min! + vitalData.max!) / 2;

            return {
              timestamp,
              date: item.month,
              dateLabel: item.month,
              aggregationLevel,
              setPausePressure: 0,
              setInspPressure: 0,
              setExpPressure: 0,
              peakInspPressure: { min: 0, max: 0, mean: 0 },
              flow: {
                min: vitalData.min!,
                max: vitalData.max!,
                mean: mean,
              },
              deliveredVolume: {
                min: vitalData.min!,
                max: vitalData.max!,
                mean: mean,
                sum: 0,
              },
              cycleCount: vitalData.count,
              sessionCount: 1,
              therapyTypes: [],
              refIds: [],
              startIndex: 0,
              endIndex: 0,
            } as AggregatedDataPoint;
          });
        return filtered;
      } else {
        const filtered = vitalsData.daily
          .filter((item) => {
            const timestamp = parseVitalsTimestamp(item.date, aggregationLevel);
            const vitalData = item[
              selectedMetric as keyof DailyVitals
            ] as VitalsDataPoint;

            // Filter 1: Time range
            if (timestamp < minTime || timestamp > maxTime) return false;

            // Filter 2: Has valid data
            return vitalData.min !== null && vitalData.max !== null;
          })
          .map((item) => {
            const timestamp = parseVitalsTimestamp(item.date, aggregationLevel);
            const vitalData = item[
              selectedMetric as keyof DailyVitals
            ] as VitalsDataPoint;
            const mean =
              vitalData.mean !== null
                ? vitalData.mean
                : (vitalData.min! + vitalData.max!) / 2;

            return {
              timestamp,
              date: item.date,
              dateLabel: item.date,
              aggregationLevel,
              setPausePressure: 0,
              setInspPressure: 0,
              setExpPressure: 0,
              peakInspPressure: { min: 0, max: 0, mean: 0 },
              flow: {
                min: vitalData.min!,
                max: vitalData.max!,
                mean: mean,
              },
              deliveredVolume: {
                min: vitalData.min!,
                max: vitalData.max!,
                mean: mean,
                sum: 0,
              },
              cycleCount: vitalData.count,
              sessionCount: 1,
              therapyTypes: [],
              refIds: [],
              startIndex: 0,
              endIndex: 0,
            } as AggregatedDataPoint;
          });

        return filtered;
      }
    } else {
      // ============================================
      // DAILY/SESSION VIEWS: Single synthetic point at middle
      // ============================================

      if (!visibleData.length) {
        // console.log("❌ No visible data for daily/session view");
        return [];
      }

      const firstPoint = visibleData[0];
      const sessionDate = new Date(firstPoint.timestamp)
        .toISOString()
        .split("T")[0];

      const dayVitals = vitalsData.daily.find((d) => d.date === sessionDate);
      if (!dayVitals) {
        // console.log(`⚠️ No vitals data for date ${sessionDate}`);
        return [];
      }

      const vitalData = dayVitals[
        selectedMetric as keyof DailyVitals
      ] as VitalsDataPoint;

      if (vitalData.min === null || vitalData.max === null) {
        console.log(
          `⚠️ Incomplete vitals data for ${selectedMetric} on ${sessionDate}`,
          vitalData
        );
        return [];
      }

      const mean =
        vitalData.mean !== null
          ? vitalData.mean
          : (vitalData.min + vitalData.max) / 2;

      const middleIndex = Math.floor(visibleData.length / 2);
      const middlePoint = visibleData[middleIndex];

      return [
        {
          timestamp: middlePoint.timestamp,
          date: sessionDate,
          dateLabel: sessionDate,
          aggregationLevel: "daily",
          setPausePressure: 0,
          setInspPressure: 0,
          setExpPressure: 0,
          peakInspPressure: { min: 0, max: 0, mean: 0 },
          flow: {
            min: vitalData.min,
            max: vitalData.max,
            mean: mean,
          },
          deliveredVolume: {
            min: vitalData.min,
            max: vitalData.max,
            mean: mean,
            sum: 0,
          },
          cycleCount: vitalData.count,
          sessionCount: 1,
          therapyTypes: [],
          refIds: [],
          startIndex: middleIndex,
          endIndex: middleIndex,
        } as AggregatedDataPoint,
      ];
    }
  };

  // Helper to get vitals data for the current hovered/selected point
  const getVitalsForCurrentPoint = (
    vitalsData: VitalsData | null,
    selectedMetric: MeasurementType,
    currentPoint: RenderableDataPoint | null,
    aggregationLevel: AggregationLevel
  ): VitalsDataPoint | null => {
    if (!vitalsData || !currentPoint || !isVitalsParameter(selectedMetric)) {
      return null;
    }

    if (isAggregated(currentPoint)) {
      // Aggregated view - match by date
      const aggPoint = currentPoint as AggregatedDataPoint;

      if (aggregationLevel === "yearly") {
        // Find monthly vitals for the hovered month
        const month = aggPoint.date; // Already in "YYYY-MM" format
        const monthlyVital = vitalsData.monthly.find((m) => m.month === month);
        if (!monthlyVital) return null;

        return monthlyVital[
          selectedMetric as keyof MonthlyVitals
        ] as VitalsDataPoint;
      } else {
        // Find daily vitals for the hovered day
        const dailyVital = vitalsData.daily.find(
          (d) => d.date === aggPoint.date
        );
        if (!dailyVital) return null;

        return dailyVital[
          selectedMetric as keyof DailyVitals
        ] as VitalsDataPoint;
      }
    } else {
      // Daily/session view - match by date (same for all cycles in that day)
      const rawPoint = currentPoint as TherapyDataPoint;
      const sessionDate = new Date(rawPoint.timestamp)
        .toISOString()
        .split("T")[0];
      const dailyVital = vitalsData.daily.find((d) => d.date === sessionDate);
      if (!dailyVital) return null;

      return dailyVital[selectedMetric as keyof DailyVitals] as VitalsDataPoint;
    }
  };

  // Unified data for hover/cursor (only in aggregated views with vitals)
  const unifiedData = useMemo(() => {
    // Only merge in aggregated views (yearly/monthly)
    if (
      (aggregationLevel === "yearly" || aggregationLevel === "monthly") &&
      isVitalsParameter(selectedMetric)
    ) {
      const vitalsRenderData = getVitalsRenderData(
        vitalsData,
        selectedMetric,
        aggregationLevel,
        visibleData,
        viewport
      );

      const merged = mergeTherapyAndVitals(visibleData, vitalsRenderData);
      return merged;
    }

    // For non-vitals or daily/session views, return therapy data as-is
    return visibleData.map((point) => ({
      date: isAggregated(point)
        ? (point as AggregatedDataPoint).date
        : new Date(point.timestamp).toISOString().split("T")[0],
      timestamp: point.timestamp,
      hasTherapy: true,
      hasVitals: false,
      therapyPoint: point,
      vitalsPoint: null,
    })) as UnifiedDataPoint[];
  }, [
    visibleData,
    vitalsData,
    selectedMetric,
    aggregationLevel,
    viewport,
    mergeTherapyAndVitals,
  ]);

  // -------------------- Render helpers --------------------
  // Main chart renderer: important differences from prior version:
  // - Flow zero aligns with pressure zero (we compute zeroY)
  // - Therapy separator labels render only when visibleData.length <= 14 (configurable)

  // ==================== UPDATED MAIN CHART WITH PAN TRACKING ====================

  const renderMainChart = (width: number, availableHeight?: number) => {
    const margins = mergedConfig.layout.margins;
    const chartWidth = Math.max(50, width - margins.left - margins.right);
    const chartHeight = availableHeight
      ? availableHeight - margins.top - margins.bottom
      : mergedConfig.layout.mainHeight - margins.top - margins.bottom;

    chartWidthRef.current = chartWidth;

    const getAxisDx = (w: number) => {
      if (w < 480) return -8;
      if (w < 768) return -6;
      return -6;
    };

    // FIXED: X SCALE - Use index-based for daily view to prevent overlap
    // X SCALE - Multi-mode: time-based, index-based, or segmented
    const xScale = (() => {
      if (aggregationLevel === "session") {
        // Session view: index-based for single session
        return scaleLinear({
          domain: [0, Math.max(1, visibleData.length - 1)],
          range: [0, chartWidth],
        });
      } else if (aggregationLevel === "daily") {
        // Daily view: segmented by sessions (handled in getXPosition)
        // Create a dummy scale - actual positioning done by segments
        return scaleLinear({
          domain: [0, chartWidth],
          range: [0, chartWidth],
        });
      } else {
        // Aggregated views: time-based scale
        return scaleTime({
          domain: viewport
            ? [viewport.start, viewport.end]
            : fullCalendarBounds
              ? [fullCalendarBounds.start, fullCalendarBounds.end]
              : [new Date(), new Date()],
          range: [0, chartWidth],
        });
      }
    })();

    // Calculate session segments for daily view
    const sessionSegments =
      aggregationLevel === "daily"
        ? calculateSessionSegments(
          therapySessions.filter((s) => {
            if (!viewport) return false;
            const sessionTime = new Date(s.startTime).getTime();
            return (
              sessionTime >= viewport.start.getTime() &&
              sessionTime <= viewport.end.getTime()
            );
          }),
          visibleData,
          chartWidth
        )
        : [];

    // Helper to get X position for a data point
    // Helper to get X position for a data point
    const getXPosition = (d: RenderableDataPoint, index: number) => {
      if (aggregationLevel === "session") {
        // Session view: simple index-based
        return xScale(index);
      } else if (aggregationLevel === "daily") {
        // Daily view: segmented by sessions
        if (!isAggregated(d)) {
          // Find which segment this point belongs to
          const segment = sessionSegments.find((seg) =>
            seg.cycleIndices.includes(index)
          );

          if (segment) {
            // Find index within this segment
            const indexInSegment = segment.cycleIndices.indexOf(index);
            const segmentScale = scaleLinear({
              domain: [0, Math.max(1, segment.cycleIndices.length - 1)],
              range: [segment.startX, segment.endX],
            });
            return segmentScale(indexInSegment);
          }
        }
        return 0;
      } else {
        // Aggregated views: time-based
        const timestamp = isAggregated(d) ? d.timestamp : d.timestamp;
        return (xScale as any)(new Date(timestamp));
      }
    };

    // Map a pointer clientX on the overlay rect to the nearest data index.
    // The overlay already sits inside the margin-translated Group, so its
    // bounding rect starts at the plot origin — no margin subtraction needed.
    // Session/daily snap to the bars' actual drawn X (getXPosition), since
    // daily bars are laid out per session segment, not evenly spaced.
    const getNearestIndexFromClientX = (
      clientX: number,
      overlayEl: Element
    ): number | null => {
      const rect = overlayEl.getBoundingClientRect();
      if (rect.width === 0) return null;
      // Convert to SVG user units in case the svg is CSS-scaled (maxWidth: 100%)
      const x = (clientX - rect.left) * (chartWidth / rect.width);

      if (aggregationLevel === "session" || aggregationLevel === "daily") {
        if (visibleData.length === 0) return null;
        const candidates =
          aggregationLevel === "daily" && sessionSegments.length > 0
            ? sessionSegments.flatMap((seg) => seg.cycleIndices)
            : visibleData.map((_, i) => i);

        let nearestIdx: number | null = null;
        let minDist = Infinity;
        for (const i of candidates) {
          const dist = Math.abs(getXPosition(visibleData[i], i) - x);
          if (dist < minDist) {
            minDist = dist;
            nearestIdx = i;
          }
        }
        return nearestIdx;
      }

      // Aggregated views: timestamp-based snap-to-nearest
      if (unifiedData.length === 0) return null;
      const hoveredTs = ((xScale as any).invert(x) as Date).getTime();
      let closestIdx = 0;
      let minDiff = Math.abs(unifiedData[0].timestamp - hoveredTs);
      for (let i = 1; i < unifiedData.length; i++) {
        const diff = Math.abs(unifiedData[i].timestamp - hoveredTs);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      return closestIdx;
    };

    const updateHoverFromClientX = (clientX: number, overlayEl: Element) => {
      const idx = getNearestIndexFromClientX(clientX, overlayEl);
      if (idx === null) return;
      setHoverIndex(idx);
      setIsHovering(true);
    };

    // Pressure scale (normal orientation)
    const pMin = (pressureExtent?.[0] ?? 0) - 10;
    const pMax = (pressureExtent?.[1] ?? 80) + 10;
    const pressureScale = scaleLinear<number>({
      domain: [pMin, pMax],
      range: [chartHeight, 0],
      nice: true,
    });

    const zeroY = pressureScale(0);

    // Right Y-axis scale (for selected metric)
    const rightYScale = useMemo(() => {
      if (selectedMetric === "none") return null;

      const config = getFieldConfig(selectedMetric);

      if (selectedMetric === "flow") {
        // EXISTING FLOW LOGIC - unchanged
        const fMin = flowExtent?.[0] ?? 0;
        const fMax = flowExtent?.[1] ?? 0;
        const maxAbsFlow = Math.max(Math.abs(fMin), Math.abs(fMax), 1);

        return scaleLinear({
          domain: [-maxAbsFlow, 0, maxAbsFlow],
          range: [chartHeight, zeroY, 0],
          nice: true,
        });
      } else if (selectedMetric === "volume") {
        // EXISTING VOLUME LOGIC - unchanged
        const vMin =
          volumeExtent && volumeExtent[0] != null ? volumeExtent[0] : 0;
        const vMax =
          volumeExtent && volumeExtent[1] != null ? volumeExtent[1] : 1;
        const maxAbsVolume = Math.max(Math.abs(vMin), Math.abs(vMax), 1);

        return scaleLinear({
          domain: [-maxAbsVolume, 0, maxAbsVolume],
          range: [chartHeight, zeroY, 0],
          nice: true,
        });
      } else if (config.isVitals) {
        // NEW: Vitals - align zero with pressure scale (same as flow/volume)
        // Use computed extent if available, otherwise fallback to config scale
        const [minVal, maxVal] = vitalsExtent ?? config.scale;

        // Calculate max absolute value to create symmetric domain around zero
        const maxAbsValue = Math.max(Math.abs(minVal), Math.abs(maxVal), 1);

        return scaleLinear({
          domain: [-maxAbsValue, 0, maxAbsValue],
          range: [chartHeight, zeroY, 0], // ✅ Same as flow/volume - aligns with pressure zero
          nice: true,
        });
      }

      return null;
    }, [
      selectedMetric,
      flowExtent,
      volumeExtent,
      vitalsExtent,
      chartHeight,
      zeroY,
    ]);

    const rightYColor = useMemo(() => {
      const config = getFieldConfig(selectedMetric);

      // For flow/volume, use colors from mergedConfig (keeps existing behavior)
      if (selectedMetric === "flow") return mergedConfig.colors.flow;
      if (selectedMetric === "volume") return mergedConfig.colors.volume;

      // For vitals and none, use config color
      return config.color;
    }, [selectedMetric, mergedConfig.colors]);

    const rightYLabel = useMemo(() => {
      const config = getFieldConfig(selectedMetric);
      return config.unit;
    }, [selectedMetric]);

    // Calculate bar width - segment-aware for daily view
    // Calculate bar width - segment-aware for daily view
    const barWidth = (() => {
      const minBarWidth = mergedConfig.features.minBarWidth || 8;
      const maxBarWidth = mergedConfig.features.maxBarWidth || 14;

      if (aggregationLevel === "daily" && sessionSegments.length > 0) {
        // For daily view, calculate bar width based on segments
        const sessionCount = sessionSegments.length;
        const cyclePadding = sessionCount > 4 ? 0.2 : 0.3;

        // Calculate bar width from smallest segment
        const minSegmentWidth = Math.min(
          ...sessionSegments.map((seg) => seg.width)
        );
        const maxCyclesInSegment = Math.max(
          ...sessionSegments.map((seg) => seg.cycleIndices.length)
        );

        const calculatedWidth =
          minSegmentWidth / maxCyclesInSegment / (1 + cyclePadding);

        return Math.max(minBarWidth, Math.min(maxBarWidth, calculatedWidth));
      }

      // Session and aggregated views
      return Math.max(
        minBarWidth,
        Math.min(maxBarWidth, chartWidth / Math.max(visibleData.length, 20) / 2)
      );
    })();

    const peakDashWidth = barWidth * 1.2;

    const generateEvenDateTicks = (start: Date, end: Date, count: number) => {
      const startMs = start.getTime();
      const endMs = end.getTime();
      const ticks = [];

      for (let i = 0; i < count; i++) {
        const t = startMs + (i / (count - 1)) * (endMs - startMs);
        ticks.push(new Date(t));
      }

      return ticks;
    };

    const isMobile = width < 600;

    return (
      <div style={{ width: "100%" }}>
        <div
          style={{
            paddingLeft: `${margins.left}px`,
            // paddingRight: `${margins.right}px`,
            marginBottom: "-40px",
            zIndex: 1,
            position: "relative",
          }}
        >
          {renderInfoPanel()}
        </div>

        <svg
          width={width}
          height={availableHeight ? availableHeight + 35 : mergedConfig.layout.mainHeight}
          style={{
            maxWidth: "100%",
            overflow: "hidden",
            position: "relative",
            zIndex: 0,
          }}
        >
          <Group
            left={margins.left}
            top={margins.top}
            style={{ touchAction: "none", overscrollBehavior: "none" }}
          >
            {/* Grid lines */}
            {mergedConfig.features.showGrid &&
              pressureScale
                .ticks(5)
                .map((t) => (
                  <line
                    key={`grid-p-${t}`}
                    x1={0}
                    x2={chartWidth}
                    y1={pressureScale(t)}
                    y2={pressureScale(t)}
                    stroke="#f0f0f0"
                    strokeWidth={1}
                  />
                ))}

            {/* Zero line */}
            <line
              x1={0}
              x2={chartWidth}
              y1={zeroY}
              y2={zeroY}
              stroke="#cbd5e1"
              strokeWidth={1.25}
            />

            {/* Day/Month boundary lines - NOT IN DAILY VIEW */}
            {aggregationLevel !== "daily" &&
              viewport &&
              (() => {
                const boundaries: Date[] = [];
                const current = new Date(viewport.start);

                if (aggregationLevel === "monthly") {
                  current.setHours(0, 0, 0, 0);
                  current.setDate(current.getDate() + 1);

                  while (current <= viewport.end) {
                    boundaries.push(new Date(current));
                    current.setDate(current.getDate() + 1);
                  }
                } else if (aggregationLevel === "yearly") {
                  current.setDate(1);
                  current.setMonth(current.getMonth() + 1);

                  while (current <= viewport.end) {
                    boundaries.push(new Date(current));
                    current.setMonth(current.getMonth() + 1);
                  }
                }

                return boundaries.map((boundary, i) => (
                  <line
                    key={`boundary-${i}`}
                    x1={(xScale as any)(boundary)}
                    x2={(xScale as any)(boundary)}
                    y1={0}
                    y2={chartHeight}
                    stroke="#e5e7eb"
                    strokeWidth={1}
                    strokeDasharray="3,3"
                  />
                ));
              })()}

            {/* Missed therapy overlays - ALL VIEWS */}
            {viewport &&
              (() => {
                const missedPeriods: { start: Date; end: Date }[] = [];

                if (aggregationLevel === "yearly") {
                  // Check each month in viewport
                  const current = new Date(viewport.start);
                  current.setDate(1);

                  while (current <= viewport.end) {
                    const monthStart = startOfMonth(current);
                    const monthEnd = endOfMonth(current);

                    // Check if this month has ANY data
                    const hasData = data.some((point) => {
                      return (
                        point.timestamp >= monthStart.getTime() &&
                        point.timestamp <= monthEnd.getTime()
                      );
                    });

                    if (!hasData) {
                      missedPeriods.push({ start: monthStart, end: monthEnd });
                    }

                    current.setMonth(current.getMonth() + 1);
                  }
                } else if (aggregationLevel === "monthly") {
                  // Check each day in viewport
                  const current = new Date(viewport.start);
                  current.setHours(0, 0, 0, 0);

                  while (current <= viewport.end) {
                    const dayStart = startOfDay(current);
                    const dayEnd = endOfDay(current);

                    // Check if this day has ANY data
                    const hasData = data.some((point) => {
                      return (
                        point.timestamp >= dayStart.getTime() &&
                        point.timestamp <= dayEnd.getTime()
                      );
                    });

                    if (!hasData) {
                      missedPeriods.push({ start: dayStart, end: dayEnd });
                    }

                    current.setDate(current.getDate() + 1);
                  }
                } else {
                  // Daily: Check each day in viewport
                  const current = new Date(viewport.start);
                  current.setHours(0, 0, 0, 0);

                  while (current <= viewport.end) {
                    const dayStart = startOfDay(current);
                    const dayEnd = endOfDay(current);

                    // Check if this day has ANY data
                    const hasData = data.some((point) => {
                      return (
                        point.timestamp >= dayStart.getTime() &&
                        point.timestamp <= dayEnd.getTime()
                      );
                    });

                    if (!hasData) {
                      missedPeriods.push({ start: dayStart, end: dayEnd });
                    }

                    current.setDate(current.getDate() + 1);
                  }
                }

                return missedPeriods.map((period, i) => {
                  const startX = xScale(period.start);
                  const endX = xScale(period.end);
                  const width = Math.max(1, endX - startX);

                  return (
                    <rect
                      key={`missed-period-${i}`}
                      x={startX}
                      width={width}
                      y={0}
                      height={chartHeight}
                      fill={mergedConfig.colors.missedTherapyOverlay}
                      pointerEvents="none"
                    />
                  );
                });
              })()}

            {/* Session gap overlays - DAILY VIEW ONLY */}
            {aggregationLevel === "daily" &&
              sessionSegments.map((segment, i) => (
                <React.Fragment key={`session-gap-${i}`}>
                  {/* Gap before first session */}
                  {i === 0 && (
                    <rect
                      x={0}
                      y={0}
                      width={segment.startX}
                      height={chartHeight}
                      fill={mergedConfig.colors.missedTherapyOverlay}
                      pointerEvents="none"
                    />
                  )}
                  {/* Gap after this session */}
                  <rect
                    x={segment.endX}
                    y={0}
                    width={segment.gapAfter}
                    height={chartHeight}
                    fill={mergedConfig.colors.missedTherapyOverlay}
                    pointerEvents="none"
                  />
                </React.Fragment>
              ))}

            {/* Empty state overlay with time axis */}
            {!(hasDataInViewport || hasVitalsInViewport) && viewport && (
              <Group>
                <rect
                  x={0}
                  y={0}
                  width={chartWidth}
                  height={chartHeight}
                  fill={mergedConfig.colors.missedTherapyOverlay}
                  opacity={0.8}
                />
                <text
                  x={chartWidth / 2}
                  y={chartHeight / 2 - 20}
                  fontSize={14}
                  fill="#9ca3af"
                  textAnchor="middle"
                  fontWeight={500}
                >
                  No Therapy Data
                </text>
                <text
                  x={chartWidth / 2}
                  y={chartHeight / 2}
                  fontSize={12}
                  fill="#9ca3af"
                  textAnchor="middle"
                >
                  {(() => {
                    const start = viewport.start;
                    const end = viewport.end;

                    if (aggregationLevel === "yearly") {
                      if (start.getFullYear() === end.getFullYear()) {
                        return `${start.toLocaleDateString(undefined, {
                          month: "short",
                          year: "numeric",
                        })} - ${end.toLocaleDateString(undefined, {
                          month: "short",
                          year: "numeric",
                        })}`;
                      }
                      return `${start.toLocaleDateString(undefined, {
                        year: "numeric",
                      })} - ${end.toLocaleDateString(undefined, {
                        year: "numeric",
                      })}`;
                    } else if (aggregationLevel === "monthly") {
                      if (start.getMonth() === end.getMonth()) {
                        return start.toLocaleDateString(undefined, {
                          month: "long",
                          year: "numeric",
                        });
                      }
                      return `${start.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })} - ${end.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`;
                    } else {
                      if (start.toDateString() === end.toDateString()) {
                        return start.toLocaleDateString(undefined, {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        });
                      }
                      return `${start.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })} - ${end.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`;
                    }
                  })()}
                </text>
                <text
                  x={chartWidth / 2}
                  y={chartHeight / 2 + 20}
                  fontSize={11}
                  fill="#9ca3af"
                  textAnchor="middle"
                >
                  Pan left or right to view other periods
                </text>
              </Group>
            )}

            {/* 1. LAYER 1: Set pressure bars (bottom) */}
            {hasDataInViewport &&
              visibleData.map((d, i) => {
                const x = getXPosition(d, i);
                const baseZero = pressureScale(0);

                const setPause = isAggregated(d)
                  ? d.setPausePressure
                  : d.setPausePressure;
                const setInsp = isAggregated(d)
                  ? d.setInspPressure
                  : d.setInspPressure;
                const setExp = isAggregated(d)
                  ? d.setExpPressure
                  : d.setExpPressure;

                const yPause = pressureScale(setPause);
                const hPause = Math.abs(yPause - baseZero);
                const yInsp = pressureScale(setInsp);
                const hInsp = Math.abs(yInsp - baseZero);
                const yExp = pressureScale(setExp);
                const hExp = Math.abs(yExp - baseZero);

                const timestamp = isAggregated(d) ? d.timestamp : d.timestamp;

                return (
                  <g
                    key={`bars-${i}-${timestamp}`}
                    style={{ touchAction: "none" }}
                  >
                    <Bar
                      x={x - barWidth * 1.6}
                      y={Math.min(yPause, baseZero)}
                      width={barWidth}
                      height={hPause}
                      fill={mergedConfig.colors.setPause}
                    />
                    <Bar
                      x={x - barWidth * 0.5}
                      y={Math.min(yInsp, baseZero)}
                      width={barWidth}
                      height={hInsp}
                      fill={mergedConfig.colors.setInsp}
                    />
                    <Bar
                      x={x + barWidth * 0.6}
                      y={Math.min(yExp, baseZero)}
                      width={barWidth}
                      height={hExp}
                      fill={mergedConfig.colors.setExp}
                    />
                  </g>
                );
              })}

            {/*2. LAYER 2: secondary metric rendering (middle)  */}
            {(hasDataInViewport || hasVitalsInViewport) &&
              rightYScale &&
              selectedMetric !== "none" &&
              (() => {
                // ============================================
                // DETERMINE DATA SOURCE
                // ============================================
                const isVitals = isVitalsParameter(selectedMetric);
                const renderData = isVitals
                  ? getVitalsRenderData(
                    vitalsData,
                    selectedMetric,
                    aggregationLevel,
                    visibleData,
                    viewport
                  )
                  : visibleData;

                // Early return if no data
                if (renderData.length === 0) return null;

                // ============================================
                // RENDERING LOGIC (SAME FOR BOTH)
                // ============================================
                if (aggregationLevel === "daily") {
                  // Daily view rendering
                  if (isVitals) {
                    // For vitals: single point at middle (renderData has 1 element)
                    const vitalPoint = renderData[0] as AggregatedDataPoint;
                    const middleIndex = Math.floor(visibleData.length / 2);
                    const x = getXPosition(
                      visibleData[middleIndex],
                      middleIndex
                    );

                    const stats =
                      selectedMetric === "flow"
                        ? vitalPoint.flow
                        : vitalPoint.deliveredVolume;
                    const minY = rightYScale(stats.min);
                    const maxY = rightYScale(stats.max);
                    const meanY = rightYScale(stats.mean);
                    const rhombusSize = barWidth / 4;

                    return (
                      <Group key="vitals-daily-point">
                        {/* Vertical line */}
                        <line
                          x1={x}
                          x2={x}
                          y1={minY}
                          y2={maxY}
                          stroke={rightYColor}
                          strokeWidth={1}
                          opacity={0.6}
                        />

                        {/* Min rhombus (hollow) */}
                        <polygon
                          points={`${x},${minY - rhombusSize} ${x + rhombusSize
                            },${minY} ${x},${minY + rhombusSize} ${x - rhombusSize
                            },${minY}`}
                          fill="none"
                          stroke={rightYColor}
                          strokeWidth={1.5}
                        />

                        {/* Max rhombus (filled) */}
                        <polygon
                          points={`${x},${maxY - rhombusSize} ${x + rhombusSize
                            },${maxY} ${x},${maxY + rhombusSize} ${x - rhombusSize
                            },${maxY}`}
                          fill={rightYColor}
                          stroke={rightYColor}
                          strokeWidth={1}
                        />

                        {/* Mean dash */}
                        <line
                          x1={x - peakDashWidth / 2}
                          x2={x + peakDashWidth / 2}
                          y1={meanY}
                          y2={meanY}
                          stroke={rightYColor}
                          strokeWidth={2.5}
                          strokeLinecap="round"
                        />
                      </Group>
                    );
                  } else {
                    // Therapy: Existing segmented rendering with dashes
                    return (
                      <>
                        {sessionSegments.map((segment, segIdx) => {
                          const segmentData = segment.cycleIndices
                            .map((idx) => ({ data: renderData[idx], idx }))
                            .filter((item) => !isAggregated(item.data));

                          if (segmentData.length === 0) return null;

                          return (
                            <Group key={`metric-segment-${segIdx}`}>
                              <LinePath
                                data={segmentData}
                                x={(item) => getXPosition(item.data, item.idx)}
                                y={(item) => {
                                  const point = item.data as TherapyDataPoint;
                                  let value =
                                    selectedMetric === "flow"
                                      ? point.flow
                                      : point.deliveredVolume;
                                  if (needsFlip(selectedMetric)) value = -value;
                                  return rightYScale(value ?? 0);
                                }}
                                stroke={rightYColor}
                                strokeWidth={1}
                                strokeDasharray="4,4"
                                curve={curveLinear}
                              />

                              {segmentData.map((item) => {
                                const point = item.data as TherapyDataPoint;
                                const x = getXPosition(item.data, item.idx);
                                let value =
                                  selectedMetric === "flow"
                                    ? point.flow
                                    : point.deliveredVolume;
                                if (needsFlip(selectedMetric)) value = -value;
                                const y = rightYScale(value ?? 0);

                                return (
                                  <line
                                    key={`metric-dash-${item.idx}`}
                                    x1={x - peakDashWidth / 2}
                                    x2={x + peakDashWidth / 2}
                                    y1={y}
                                    y2={y}
                                    stroke={rightYColor}
                                    strokeWidth={2.5}
                                    strokeLinecap="round"
                                  />
                                );
                              })}
                            </Group>
                          );
                        })}
                      </>
                    );
                  }
                } else if (aggregationLevel === "session") {
                  // Session view rendering
                  if (isVitals) {
                    // For vitals: single point at middle
                    const vitalPoint = renderData[0] as AggregatedDataPoint;
                    const middleIndex = Math.floor(visibleData.length / 2);
                    const x = getXPosition(
                      visibleData[middleIndex],
                      middleIndex
                    );

                    const stats =
                      selectedMetric === "flow"
                        ? vitalPoint.flow
                        : vitalPoint.deliveredVolume;
                    const minY = rightYScale(stats.min);
                    const maxY = rightYScale(stats.max);
                    const meanY = rightYScale(stats.mean);
                    const rhombusSize = barWidth / 4;

                    return (
                      <Group key="vitals-session-point">
                        <line
                          x1={x}
                          x2={x}
                          y1={minY}
                          y2={maxY}
                          stroke={rightYColor}
                          strokeWidth={1}
                          opacity={0.6}
                        />
                        <polygon
                          points={`${x},${minY - rhombusSize} ${x + rhombusSize
                            },${minY} ${x},${minY + rhombusSize} ${x - rhombusSize
                            },${minY}`}
                          fill="none"
                          stroke={rightYColor}
                          strokeWidth={1.5}
                        />
                        <polygon
                          points={`${x},${maxY - rhombusSize} ${x + rhombusSize
                            },${maxY} ${x},${maxY + rhombusSize} ${x - rhombusSize
                            },${maxY}`}
                          fill={rightYColor}
                          stroke={rightYColor}
                          strokeWidth={1}
                        />
                        <line
                          x1={x - peakDashWidth / 2}
                          x2={x + peakDashWidth / 2}
                          y1={meanY}
                          y2={meanY}
                          stroke={rightYColor}
                          strokeWidth={2.5}
                          strokeLinecap="round"
                        />
                      </Group>
                    );
                  } else {
                    // Therapy: Existing session view with dashes
                    const sessionData = renderData
                      .map((d, idx) => ({ data: d, idx }))
                      .filter((item) => !isAggregated(item.data));

                    return (
                      <>
                        <LinePath
                          data={sessionData}
                          x={(item) => getXPosition(item.data, item.idx)}
                          y={(item) => {
                            const point = item.data as TherapyDataPoint;
                            let value =
                              selectedMetric === "flow"
                                ? point.flow
                                : point.deliveredVolume;
                            if (needsFlip(selectedMetric)) value = -value;
                            return rightYScale(value ?? 0);
                          }}
                          stroke={rightYColor}
                          strokeWidth={1}
                          strokeDasharray="4,4"
                          curve={curveLinear}
                        />

                        {sessionData.map((item) => {
                          const point = item.data as TherapyDataPoint;
                          const x = getXPosition(item.data, item.idx);
                          let value =
                            selectedMetric === "flow"
                              ? point.flow
                              : point.deliveredVolume;
                          if (needsFlip(selectedMetric)) value = -value;
                          const y = rightYScale(value ?? 0);

                          return (
                            <line
                              key={`metric-dash-${item.idx}`}
                              x1={x - peakDashWidth / 2}
                              x2={x + peakDashWidth / 2}
                              y1={y}
                              y2={y}
                              stroke={rightYColor}
                              strokeWidth={2.5}
                              strokeLinecap="round"
                            />
                          );
                        })}
                      </>
                    );
                  }
                } else {
                  // Aggregated views (yearly/monthly): whiskers with rhombus markers
                  return (
                    <>
                      {/* Dotted line connecting means with gaps */}
                      {(() => {
                        const segments: { start: number; end: number }[] = [];
                        let segmentStart = 0;

                        for (let i = 1; i < renderData.length; i++) {
                          const prevPoint = renderData[i - 1];
                          const currPoint = renderData[i];

                          const prevTime = new Date(prevPoint.timestamp);
                          const currTime = new Date(currPoint.timestamp);

                          const daysDiff = Math.abs(
                            (currTime.getTime() - prevTime.getTime()) /
                            (1000 * 60 * 60 * 24)
                          );

                          const expectedInterval =
                            aggregationLevel === "yearly" ? 31 : 1;
                          if (daysDiff > expectedInterval * 1.5) {
                            segments.push({ start: segmentStart, end: i - 1 });
                            segmentStart = i;
                          }
                        }
                        segments.push({
                          start: segmentStart,
                          end: renderData.length - 1,
                        });

                        return segments.map((seg, segIdx) => {
                          const segmentData = renderData.slice(
                            seg.start,
                            seg.end + 1
                          );
                          return (
                            <LinePath
                              key={`metric-mean-segment-${segIdx}`}
                              data={segmentData}
                              x={(d) => (xScale as any)(new Date(d.timestamp))}
                              y={(d) => {
                                if (isAggregated(d)) {
                                  const stats =
                                    selectedMetric === "flow"
                                      ? d.flow
                                      : d.deliveredVolume;
                                  const meanValue = needsFlip(selectedMetric)
                                    ? -stats.mean
                                    : stats.mean;
                                  return rightYScale(meanValue);
                                }
                                let value =
                                  selectedMetric === "flow"
                                    ? d.flow
                                    : d.deliveredVolume;
                                if (needsFlip(selectedMetric)) value = -value;
                                return rightYScale(value ?? 0);
                              }}
                              stroke={rightYColor}
                              strokeWidth={1}
                              strokeDasharray="4,4"
                              curve={curveLinear}
                            />
                          );
                        });
                      })()}

                      {/* Whiskers with rhombus markers */}
                      {renderData.map((d, i) => {
                        if (isAggregated(d)) {
                          const x = getXPosition(d, i);
                          const stats =
                            selectedMetric === "flow"
                              ? d.flow
                              : d.deliveredVolume;

                          const minValue = needsFlip(selectedMetric)
                            ? -stats.min
                            : stats.min;
                          const maxValue = needsFlip(selectedMetric)
                            ? -stats.max
                            : stats.max;
                          const meanValue = needsFlip(selectedMetric)
                            ? -stats.mean
                            : stats.mean;

                          const minY = rightYScale(minValue);
                          const maxY = rightYScale(maxValue);
                          const meanY = rightYScale(meanValue);
                          const rhombusSize = barWidth / 4;

                          return (
                            <Group key={`metric-whisker-${i}`}>
                              <line
                                x1={x}
                                x2={x}
                                y1={minY}
                                y2={maxY}
                                stroke={rightYColor}
                                strokeWidth={1}
                                opacity={0.6}
                              />
                              <polygon
                                points={`${x},${minY - rhombusSize} ${x + rhombusSize
                                  },${minY} ${x},${minY + rhombusSize} ${x - rhombusSize
                                  },${minY}`}
                                fill="none"
                                stroke={rightYColor}
                                strokeWidth={1.5}
                              />
                              <polygon
                                points={`${x},${maxY - rhombusSize} ${x + rhombusSize
                                  },${maxY} ${x},${maxY + rhombusSize} ${x - rhombusSize
                                  },${maxY}`}
                                fill={rightYColor}
                                stroke={rightYColor}
                                strokeWidth={1}
                              />
                              <line
                                x1={x - peakDashWidth / 2}
                                x2={x + peakDashWidth / 2}
                                y1={meanY}
                                y2={meanY}
                                stroke={rightYColor}
                                strokeWidth={2.5}
                                strokeLinecap="round"
                              />
                            </Group>
                          );
                        }
                        return null;
                      })}
                    </>
                  );
                }
              })()}

            {/* 3. LAYER 3: Peak inspiration markers (top) */}
            {/* Peak pressure - horizontal dashes with connection line */}
            {(() => {
              if (aggregationLevel === "daily") {
                // Daily view: segment-based peak lines
                return (
                  <>
                    {sessionSegments.map((segment, segIdx) => {
                      const segmentData = segment.cycleIndices
                        .map((idx) => ({ data: visibleData[idx], idx }))
                        .filter((item) => !isAggregated(item.data));

                      if (segmentData.length === 0) return null;

                      // NEW: Determine if segment has any dangerous points
                      const segmentIsDangerous = segmentData.some((item) => {
                        const point = item.data as TherapyDataPoint;
                        const color = getPeakPressureColor(
                          point.peakInspPressure,
                          point.setInspPressure,
                          thresholdConfig
                        );
                        return color === thresholdConfig.colors.danger;
                      });

                      const segmentLineColor = segmentIsDangerous
                        ? thresholdConfig.colors.normal
                        : thresholdConfig.colors.normal;

                      return (
                        <g key={`peak-segment-${segIdx}`}>
                          {/* Dotted connection line */}
                          <LinePath
                            data={segmentData}
                            x={(item) => getXPosition(item.data, item.idx)}
                            y={(item) =>
                              pressureScale(
                                (item.data as TherapyDataPoint).peakInspPressure
                              )
                            }
                            stroke={segmentLineColor} // ← NEW: Dynamic color by segment
                            strokeWidth={1}
                            strokeDasharray="4,4"
                            curve={curveLinear}
                          />

                          {/* Individual dashes */}
                          {segmentData.map((item) => {
                            const point = item.data as TherapyDataPoint;
                            const x = getXPosition(item.data, item.idx);
                            const y = pressureScale(point.peakInspPressure);

                            // NEW: Calculate color for each dash
                            const dashColor = getPeakPressureColor(
                              point.peakInspPressure,
                              point.setInspPressure,
                              thresholdConfig
                            );

                            return (
                              <line
                                key={`peak-dash-${item.idx}`}
                                x1={x - peakDashWidth / 2}
                                x2={x + peakDashWidth / 2}
                                y1={y}
                                y2={y}
                                stroke={dashColor} // ← NEW: Dynamic color per dash
                                strokeWidth={2.5}
                                strokeLinecap="round"
                              />
                            );
                          })}
                        </g>
                      );
                    })}
                  </>
                );
              } else if (aggregationLevel === "session") {
                const sessionData = visibleData
                  .map((d, idx) => ({ data: d, idx }))
                  .filter((item) => !isAggregated(item.data));

                // NEW: Check if any point in session is dangerous
                const sessionIsDangerous = sessionData.some((item) => {
                  const point = item.data as TherapyDataPoint;
                  const color = getPeakPressureColor(
                    point.peakInspPressure,
                    point.setInspPressure,
                    thresholdConfig
                  );
                  return color === thresholdConfig.colors.danger;
                });

                const sessionLineColor = sessionIsDangerous
                  ? thresholdConfig.colors.normal
                  : thresholdConfig.colors.normal;

                return (
                  <>
                    {/* Dotted connection line */}
                    <LinePath
                      data={sessionData}
                      x={(item) => getXPosition(item.data, item.idx)}
                      y={(item) =>
                        pressureScale(
                          (item.data as TherapyDataPoint).peakInspPressure
                        )
                      }
                      stroke={sessionLineColor} // ← NEW: Dynamic color
                      strokeWidth={1}
                      strokeDasharray="4,4"
                      curve={curveLinear}
                    />

                    {/* Individual dashes */}
                    {sessionData.map((item) => {
                      const point = item.data as TherapyDataPoint;
                      const x = getXPosition(item.data, item.idx);
                      const y = pressureScale(point.peakInspPressure);

                      // NEW: Calculate color for each dash
                      const dashColor = getPeakPressureColor(
                        point.peakInspPressure,
                        point.setInspPressure,
                        thresholdConfig
                      );

                      return (
                        <line
                          key={`peak-dash-${item.idx}`}
                          x1={x - peakDashWidth / 2}
                          x2={x + peakDashWidth / 2}
                          y1={y}
                          y2={y}
                          stroke={dashColor} // ← NEW: Dynamic color per dash
                          strokeWidth={2.5}
                          strokeLinecap="round"
                        />
                      );
                    })}
                  </>
                );
              } else {
                // Aggregated views: whiskers with rhombus markers and horizontal dashes
                return (
                  <>
                    {/* Dotted connection line for aggregated means - WITH GAP DETECTION */}
                    {(() => {
                      const segments: { start: number; end: number }[] = [];
                      let segmentStart = 0;

                      for (let i = 1; i < visibleData.length; i++) {
                        const prevPoint = visibleData[i - 1];
                        const currPoint = visibleData[i];

                        const prevTime = new Date(
                          isAggregated(prevPoint)
                            ? prevPoint.timestamp
                            : prevPoint.timestamp
                        );
                        const currTime = new Date(
                          isAggregated(currPoint)
                            ? currPoint.timestamp
                            : currPoint.timestamp
                        );

                        const daysDiff = Math.abs(
                          (currTime.getTime() - prevTime.getTime()) /
                          (1000 * 60 * 60 * 24)
                        );

                        const expectedInterval =
                          aggregationLevel === "yearly" ? 31 : 1;
                        if (daysDiff > expectedInterval * 1.5) {
                          segments.push({ start: segmentStart, end: i - 1 });
                          segmentStart = i;
                        }
                      }
                      segments.push({
                        start: segmentStart,
                        end: visibleData.length - 1,
                      });

                      return segments.map((seg, segIdx) => {
                        const segmentData = visibleData.slice(
                          seg.start,
                          seg.end + 1
                        );

                        // Check if segment has any dangerous aggregated points
                        const segmentIsDangerous = segmentData.some((d) => {
                          if (isAggregated(d)) {
                            const color = getPeakPressureColor(
                              d.peakInspPressure.mean,
                              d.setInspPressure,
                              thresholdConfig
                            );
                            return color === thresholdConfig.colors.danger;
                          }
                          return false;
                        });

                        const segmentLineColor = segmentIsDangerous
                          ? thresholdConfig.colors.normal
                          : thresholdConfig.colors.normal;

                        return (
                          <LinePath
                            key={`peak-mean-segment-${segIdx}`}
                            data={segmentData}
                            x={(d) => {
                              const timestamp = isAggregated(d)
                                ? d.timestamp
                                : d.timestamp;
                              return (xScale as any)(new Date(timestamp));
                            }}
                            y={(d) => {
                              if (isAggregated(d)) {
                                return pressureScale(d.peakInspPressure.mean);
                              }
                              return pressureScale(d.peakInspPressure ?? 0);
                            }}
                            stroke={segmentLineColor}
                            strokeWidth={1}
                            strokeDasharray="4,4"
                            curve={curveLinear}
                          />
                        );
                      });
                    })()}

                    {/* Whiskers with rhombus markers */}
                    {visibleData.map((d, i) => {
                      if (isAggregated(d)) {
                        const x = getXPosition(d, i);
                        const minY = pressureScale(d.peakInspPressure.min);
                        const maxY = pressureScale(d.peakInspPressure.max);
                        const meanY = pressureScale(d.peakInspPressure.mean);

                        // Calculate color for entire whisker based on mean
                        const whiskerColor = getPeakPressureColor(
                          d.peakInspPressure.mean,
                          d.setInspPressure, // Mean of set insp pressures
                          thresholdConfig
                        );

                        // Use barWidth for rhombus size (same as set insp bar)
                        const rhombusSize = barWidth / 4; // Half-width/height of rhombus

                        return (
                          <g key={`peak-whisker-${i}`}>
                            {/* 1. BOTTOM LAYER: Vertical line (thinner) */}
                            <line
                              x1={x}
                              x2={x}
                              y1={minY}
                              y2={maxY}
                              stroke={whiskerColor}
                              strokeWidth={1}
                              opacity={0.6}
                            />

                            {/* 2. MIDDLE LAYER: Min rhombus (hollow) */}
                            <polygon
                              points={`${x},${minY - rhombusSize} ${x + rhombusSize
                                },${minY} ${x},${minY + rhombusSize} ${x - rhombusSize
                                },${minY}`}
                              fill="none"
                              stroke={whiskerColor}
                              strokeWidth={1.5}
                            />

                            {/* 3. MIDDLE LAYER: Max rhombus (filled) */}
                            <polygon
                              points={`${x},${maxY - rhombusSize} ${x + rhombusSize
                                },${maxY} ${x},${maxY + rhombusSize} ${x - rhombusSize
                                },${maxY}`}
                              fill={whiskerColor}
                              stroke={whiskerColor}
                              strokeWidth={1}
                            />

                            {/* 4. TOP LAYER: Mean dash */}
                            <line
                              x1={x - peakDashWidth / 2}
                              x2={x + peakDashWidth / 2}
                              y1={meanY}
                              y2={meanY}
                              stroke={whiskerColor}
                              strokeWidth={2.5}
                              strokeLinecap="round"
                            />
                          </g>
                        );
                      }
                      return null;
                    })}
                  </>
                );
              }
            })()}

            {/* Synchronized cursor line on main chart */}
            {(hasDataInViewport || hasVitalsInViewport) &&
              mergedConfig.features.cursorOn?.main &&
              isHovering &&
              hoverIndex !== null &&
              hoverIndex >= 0 &&
              (() => {
                let x: number;

                if (
                  aggregationLevel === "session" ||
                  aggregationLevel === "daily"
                ) {
                  // Daily/Session view: use visibleData and getXPosition
                  if (hoverIndex >= visibleData.length) return null;
                  const point = visibleData[hoverIndex];
                  x = getXPosition(point, hoverIndex);
                } else {
                  // Aggregated views: use unified data and xScale
                  if (hoverIndex >= unifiedData.length) return null;
                  const unifiedPoint = unifiedData[hoverIndex];
                  x = (xScale as any)(
                    new Date(unifiedPoint.timestamp)
                  ) as number;

                  // Bounds check
                  if (x < 0 || x > chartWidth) return null;
                }

                return (
                  <>
                    {/* Vertical cursor line */}
                    <line
                      x1={x}
                      x2={x}
                      y1={10}
                      y2={chartHeight}
                      stroke={mergedConfig.colors.cursorColor}
                      strokeDasharray="4,4"
                      strokeWidth={2}
                    />

                    {/* Solid triangle pointer (radio dial style) */}
                    <polygon
                      points={`
                      ${x},10 
                      ${x - 8},0
                      ${x + 8},0
                      `}
                      fill={mergedConfig.colors.cursorColor}
                    />
                  </>
                );
              })()}
            {/* Left pressure axis */}
            <Group left={-margins.left} top={0}>
              <AxisLeft
                scale={pressureScale}
                left={isSmallScreen ? margins.left - 12 : margins.left - 26}
                numTicks={6}
                stroke="#9ca3af"
                strokeWidth={1}
                tickStroke="#9ca3af"
                tickLabelProps={() => ({
                  fontSize: 10,
                  fill: "#666",
                  textAnchor: "end",
                  dx: getAxisDx(width),
                })}
              />
              <text
                x={margins.left - 26}
                y={-18}
                fontSize={12}
                fill="#666"
                textAnchor="middle"
              >
                cm H<tspan baselineShift="sub">2</tspan>O
              </text>
            </Group>

            {/* Right Y-axis (conditional metric) */}
            {rightYScale && (
              <Group left={chartWidth} top={0}>
                <AxisRight
                  scale={rightYScale}
                  left={isSmallScreen ? 12 : 26}
                  numTicks={5}
                  stroke="#9ca3af"
                  strokeWidth={1}
                  tickStroke="#9ca3af"
                  tickLabelProps={() => ({
                    fontSize: 10,
                    fill: "#666",
                    textAnchor: "start",
                    dx: Math.abs(getAxisDx(width)),
                  })}
                />
                <text
                  x={margins.right - 24}
                  y={-18}
                  fontSize={12}
                  fill="#666"
                  textAnchor="middle"
                >
                  {rightYLabel}
                </text>
              </Group>
            )}
            <Group top={chartHeight + 6} width={chartWidth}>
              <AxisBottom
                scale={xScale}
                top={0}
                numTicks={(() => {
                  if (aggregationLevel === "daily") {
                    return width < 480 ? 4 : width < 768 ? 6 : 8;

                    // const minTickSpacing =
                    //   width < 480 ? 80 : width < 768 ? 70 : 60;
                    // const maxTicks = Math.floor(chartWidth / minTickSpacing);
                    // return Math.min(visibleData.length, Math.max(3, maxTicks));
                  } else if (aggregationLevel === "monthly" && viewport) {
                    return width < 480 ? 6 : width < 768 ? 8 : 10;
                  } else if (aggregationLevel === "yearly" && viewport) {
                    return width < 480 ? 6 : width < 768 ? 12 : 12;
                  }
                })()}
                tickValues={(() => {
                  if (aggregationLevel === "session") {
                    // Session view: show timestamps at regular intervals
                    const minTickSpacing =
                      width < 480 ? 80 : width < 768 ? 70 : 60;
                    const maxTicks = Math.floor(chartWidth / minTickSpacing);
                    const numTicks = Math.min(
                      visibleData.length,
                      Math.max(3, maxTicks)
                    );

                    if (numTicks >= visibleData.length) {
                      return Array.from(
                        { length: visibleData.length },
                        (_, i) => i
                      );
                    }

                    const step = Math.max(
                      1,
                      Math.floor(visibleData.length / (numTicks - 1))
                    );
                    const indices: number[] = [];
                    for (let i = 0; i < visibleData.length; i += step)
                      indices.push(i);
                    if (indices[indices.length - 1] !== visibleData.length - 1)
                      indices.push(visibleData.length - 1);
                    return indices;
                  } else if (aggregationLevel === "daily") {
                    // Daily view: show session start times at segment boundaries
                    return sessionSegments.map((seg) => seg.startX);
                  } else if (aggregationLevel === "monthly" && viewport) {
                    const tickCount = width < 480 ? 4 : 8;
                    return generateEvenDateTicks(
                      viewport.start,
                      viewport.end,
                      tickCount
                    );
                  } else if (aggregationLevel === "yearly") {
                    const year = viewport
                      ? new Date(viewport.start).getFullYear()
                      : new Date().getFullYear();

                    // All 12 months
                    const months = Array.from(
                      { length: 12 },
                      (_, m) => new Date(year, m, 1)
                    );

                    // MOBILE VIEW (<480px)
                    if (width < 480) {
                      // Always keep Jan + Dec
                      const jan = new Date(year, 0, 1);
                      const dec = new Date(year, 11, 1);

                      // Choose good intermediate months: April, July, October
                      const apr = new Date(year, 3, 1);
                      const jul = new Date(year, 6, 1);
                      const oct = new Date(year, 9, 1);

                      return [jan, apr, jul, oct, dec]; // 5 well-spaced ticks
                    }

                    // TABLET + DESKTOP
                    return months; // all 12 ticks
                  }
                })()}
                tickFormat={(value) => {
                  if (aggregationLevel === "session") {
                    const index = Math.round(value as number);
                    if (index >= 0 && index < visibleData.length) {
                      const point = visibleData[index] as TherapyDataPoint;
                      const date = new Date(point.timestamp);
                      // Show actual clock time
                      return date.toLocaleTimeString(undefined, {
                        hour: width < 480 ? "numeric" : "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    }
                    return "";
                  } else if (aggregationLevel === "daily") {
                    // Find which segment this x position corresponds to
                    const x = value as number;
                    const segment = sessionSegments.find(
                      (seg) => Math.abs(seg.startX - x) < 1
                    );
                    if (segment) {
                      const date = new Date(segment.session.startTime);
                      return date.toLocaleTimeString(undefined, {
                        hour: width < 480 ? "numeric" : "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                    }
                    return "";
                  } else if (aggregationLevel === "monthly") {
                    const date = new Date(value as number);
                    return date.getDate().toString();
                  } else if (aggregationLevel === "yearly") {
                    const date = new Date(value as number);
                    return date.toLocaleDateString(undefined, {
                      month: "short",
                    });
                  }
                  return "";
                }}
                tickLabelProps={() => ({
                  fontSize: width < 480 ? 9 : 10,
                  fill: "#666",
                  textAnchor: "middle",
                })}
              />

              {/* ===== CONTEXT LABEL (RANGE TEXT) ===== */}
              {viewport && (
                <text
                  x={chartWidth / 2}
                  y={50} // UPDATED POSITION
                  fontSize={13} // Increased from 11
                  fontWeight={600} // Added bold
                  fill="#374151" // Darker (was #888)
                  textAnchor="middle"
                >
                  {(() => {
                    const start = viewport.start;
                    const end = viewport.end;

                    if (aggregationLevel === "session") {
                      // Show date and session start time
                      const session = therapySessions.find(
                        (s) => s.refId === currentSessionRefId
                      );
                      if (session) {
                        const sessionDate = new Date(session.startTime);
                        const dateStr = sessionDate.toLocaleDateString(
                          undefined,
                          {
                            month: width < 480 ? "short" : "long",
                            day: "numeric",
                            year: "numeric",
                          }
                        );
                        if (width > 600) {
                          // Non-mobile: show "Started" time
                          return `${dateStr} · Started ${sessionDate.toLocaleTimeString(
                            undefined,
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}`;
                        } else {
                          // Mobile: just show date
                          return dateStr;
                        }
                      }
                    } else if (aggregationLevel === "yearly") {
                      return start.getFullYear() === end.getFullYear()
                        ? start.getFullYear().toString()
                        : `${start.getFullYear()} – ${end.getFullYear()}`;
                    } else if (aggregationLevel === "monthly") {
                      // Always show full date range, even if same month
                      return `${start.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })} – ${end.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`;
                    } else {
                      const sameDay =
                        start.toDateString() === end.toDateString();
                      if (sameDay) {
                        return start.toLocaleDateString(undefined, {
                          month: width < 480 ? "short" : "long",
                          day: "numeric",
                          year: "numeric",
                        });
                      }
                      return `${start.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })} – ${end.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`;
                    }
                  })()}
                </text>
              )}
            </Group>

            {/* Interactive overlay with pan support - CURSOR LINE ON TOUCH */}
            <rect
              ref={handleOverlayRef}
              x={0}
              y={0}
              width={chartWidth}
              height={chartHeight}
              fill="transparent"
              pointerEvents="all"
              style={{
                touchAction: "none",
                cursor: isDragging ? "grabbing" : "grab",
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleDragStart(e.clientX);
              }}
              onMouseMove={(e) => {
                e.stopPropagation();
                if (isDragging) {
                  handleDragMove(e.clientX, chartWidth);
                } else {
                  updateHoverFromClientX(e.clientX, e.currentTarget);
                }
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                handleDragEnd();
              }}
              onMouseLeave={() => {
                handleDragEnd();
                setIsHovering(false);
                setHoverIndex(null);
                // setHoverIndexGlobal(null);
              }}
              // 🟢 ADD THIS NEW HANDLER
              onWheel={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                handleWheelZoom(e.deltaY, e.clientX, rect);
              }}
              // 🟢 UPDATE THESE TOUCH HANDLERS
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (e.touches.length === 2) {
                  const nativeEvent = e.nativeEvent as TouchEvent;
                  handlePinchStart(
                    nativeEvent.touches[0],
                    nativeEvent.touches[1]
                  );
                } else {
                  handleDragStart(e.touches[0].clientX);
                  updateHoverFromClientX(
                    e.touches[0].clientX,
                    e.currentTarget
                  );
                }
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (e.touches.length === 2) {
                  handlePinchMove(
                    (e.nativeEvent as TouchEvent).touches[0],
                    (e.nativeEvent as TouchEvent).touches[1]
                  );
                } else if (isDragging) {
                  handleDragMove(e.touches[0].clientX, chartWidth);
                  updateHoverFromClientX(
                    e.touches[0].clientX,
                    e.currentTarget
                  );
                } else {
                  handleVolumePointerMove(e.nativeEvent);
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePinchEnd();
                handleDragEnd();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDragging && hoverIndex !== null) {
                  const point = visibleData[hoverIndex];
                  if (point && onCurrentTherapyChange) {
                    const refId = isAggregated(point)
                      ? String(point.refIds[0])
                      : String(point.refId);
                    onCurrentTherapyChange(refId);
                  }
                }
              }}
            />
          </Group>
        </svg>
      </div>
    );
  };
  // ============================================================================
  // FULLY RESPONSIVE renderInfoPanel - Dynamic Breakpoints
  // ============================================================================

  const renderInfoPanel = () => {
    const windowWidth = window.innerWidth;

    const responsive = getResponsiveLayout(windowWidth);

    let currentPoint: RenderableDataPoint | null = null;
    let currentUnified: UnifiedDataPoint | null = null;

    if (aggregationLevel === "session" || aggregationLevel === "daily") {
      // Session/Daily view: use visibleData directly
      currentPoint =
        hoverIndex !== null && visibleData[hoverIndex]
          ? visibleData[hoverIndex]
          : visibleData.length > 0
            ? visibleData[visibleData.length - 1]
            : null;
    } else {
      // Aggregated views: use unified data
      currentUnified =
        hoverIndex !== null && unifiedData[hoverIndex]
          ? unifiedData[hoverIndex]
          : unifiedData.length > 0
            ? unifiedData[unifiedData.length - 1]
            : null;

      // Extract therapy point (may be null for vitals-only dates)
      currentPoint = currentUnified?.therapyPoint ?? null;
    }

    const isAgg = currentPoint ? isAggregated(currentPoint) : false;

    // Extract therapy values (existing code)
    let setPause: number | null = null;
    let setInsp: number | null = null;
    let setExp: number | null = null;
    let peakInsp: { value: number; min?: number; max?: number } | null = null;
    let peakFlow: { value: number; min?: number; max?: number } | null = null;
    let expVolume: { value: number; min?: number; max?: number } | null = null;
    let profileName: string | null = null;
    let therapyModes: string[] = [];
    let oscillationStatus = false;
    let date: string | null = null;
    let cycleInfo: string | null = null;

    if (currentPoint) {
      if (isAgg) {
        const aggPoint = currentPoint as AggregatedDataPoint;
        setPause = aggPoint.setPausePressure;
        setInsp = aggPoint.setInspPressure;
        setExp = aggPoint.setExpPressure;

        peakInsp = {
          value: aggPoint.peakInspPressure.mean,
          min: aggPoint.peakInspPressure.min,
          max: aggPoint.peakInspPressure.max,
        };
        peakFlow = {
          value: aggPoint.flow.mean,
          min: aggPoint.flow.min,
          max: aggPoint.flow.max,
        };
        expVolume = {
          value: aggPoint.deliveredVolume.mean,
          min: aggPoint.deliveredVolume.min,
          max: aggPoint.deliveredVolume.max,
        };

        therapyModes = aggPoint.therapyTypes;
        cycleInfo = `${aggPoint.cycleCount} cycles`;

        const dateObj = new Date(aggPoint.timestamp);
        if (aggPoint.aggregationLevel === "yearly") {
          date = dateObj.toLocaleDateString(undefined, {
            month: "short",
            year: "numeric",
          });
        } else if (aggPoint.aggregationLevel === "daily") {
          date = `${dateObj.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })} ${dateObj.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })}`;
        } else {
          date = dateObj.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }
      } else {
        const rawPoint = currentPoint as TherapyDataPoint;
        setPause = rawPoint.setPausePressure;
        setInsp = rawPoint.setInspPressure;
        setExp = rawPoint.setExpPressure;

        peakInsp = { value: rawPoint.peakInspPressure || 0 };
        peakFlow = { value: rawPoint.flow || 0 };
        expVolume = { value: rawPoint.deliveredVolume || 0 };

        profileName = rawPoint.profileName || "Unknown";
        therapyModes = [rawPoint.therapyType];
        oscillationStatus = rawPoint.deliveredOscillation;
        cycleInfo = `Cycle ${rawPoint.cycle}`;

        const dateObj = new Date(rawPoint.timestamp);
        if (aggregationLevel === "daily") {
          date = `${dateObj.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          })} ${dateObj.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })}`;
        } else {
          date = dateObj.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }
      }
    }

    // Update date display for vitals-only dates (aggregated views only)
    if (!currentPoint && currentUnified) {
      const hoveredDate = new Date(currentUnified.timestamp);

      if (aggregationLevel === "yearly") {
        date = hoveredDate.toLocaleDateString(undefined, {
          month: "short",
          year: "numeric",
        });
      } else {
        date = hoveredDate.toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      }

      therapyModes = ["—"];
      cycleInfo = "No therapy data";
    }

    // ============================================
    // Extract vitals data if vitals metric selected
    // ============================================
    let vitalData: {
      value: number;
      min: number;
      max: number;
      unit: string;
      label: string;
    } | null = null;

    if (isVitalsParameter(selectedMetric)) {
      let vitalsPoint: VitalsDataPoint | null = null;

      if (aggregationLevel === "session" || aggregationLevel === "daily") {
        // Daily/Session views: look up vitals by date
        if (currentPoint) {
          const sessionDate = new Date(currentPoint.timestamp)
            .toISOString()
            .split("T")[0];
          const dayVitals = vitalsData?.daily.find(
            (d) => d.date === sessionDate
          );

          if (dayVitals) {
            // ✅ Get the full VitalsDataPoint structure (has mean, min, max)
            const vitalField = selectedMetric as keyof DailyVitals;
            const extractedVital = dayVitals[vitalField];

            // Make sure it's actually a VitalsDataPoint and not a string
            if (typeof extractedVital === "object" && extractedVital !== null) {
              vitalsPoint = extractedVital as VitalsDataPoint;
            }
          }
        }
      } else {
        // Aggregated views: use unified data
        if (currentUnified?.vitalsPoint) {
          // Vitals are stored in flow/deliveredVolume fields (from our transformation)
          const vPoint = currentUnified.vitalsPoint as AggregatedDataPoint;

          vitalsPoint = {
            mean: vPoint.flow.mean,
            min: vPoint.flow.min,
            max: vPoint.flow.max,
            count: vPoint.cycleCount,
          } as VitalsDataPoint;
        }
      }

      // ✅ Create vitalData with proper min/max (not just mean)
      if (vitalsPoint && vitalsPoint.mean !== null) {
        const config = getFieldConfig(selectedMetric);
        vitalData = {
          value: vitalsPoint.mean,
          min: vitalsPoint.min !== null ? vitalsPoint.min : vitalsPoint.mean, // Use actual min
          max: vitalsPoint.max !== null ? vitalsPoint.max : vitalsPoint.mean, // Use actual max
          unit: config.unit,
          label: config.label,
        };
      }
    }

    const formatValue = (
      val: number | null | undefined,
      min?: number,
      max?: number,
      forceShowRange = false // ✅ New parameter for vitals
    ) => {
      if (val == null) return "—";
      const base = val.toFixed(1); // ✅ Changed from 0 to 1 decimal
      if ((isAgg || forceShowRange) && min != null && max != null) {
        return `${base} (${min.toFixed(1)}-${max.toFixed(1)})`; // ✅ Changed from 0 to 1 decimal
      }
      return base;
    };

    const ColorLabel = ({ color, text }: { color: string; text: string }) => (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          marginRight: "3px",
        }}
      >
        <span
          style={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600, fontSize: "10px" }}>{text}</span>
      </span>
    );

    // Updated legend - always shows all three indicators
    const MeasuredLegend = () => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
            fontSize: "10px",
            color: "#6b7280",
            marginTop: "6px",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 600 }}>—</span>
            <span>Mean</span>
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 600 }}>◆</span>
            <span>Max</span>
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "12px", fontWeight: 600 }}>◇</span>
            <span>Min</span>
          </span>
        </div>
      );
    };

    // ============================================================================
    // MOBILE PORTRAIT LAYOUT (<600px)
    // ============================================================================
    if (responsive.layout === "mobile") {
      return (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            boxSizing: "border-box",
          }}
        >
          {/* Row 1: Right aligned dropdown */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CustomDropdown
              selected={selectedMetric}
              onChange={(value) => setSelectedMetric(value as MeasurementType)}
              options={MEASUREMENT_OPTIONS}
              minWidth={responsive.dropdownWidth}
            />
          </div>

          {/* Row 2: Full-width stacked tooltip box */}
          <div
            style={{
              width: "80%",
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              boxSizing: "border-box",
            }}
          >
            {/* SESSION INFO - Now First */}
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Session Info
              </div>
              <div
                style={{
                  fontSize: responsive.fontSize,
                  color: "#1F2937",
                  lineHeight: 1.5,
                }}
              >
                <strong>Time:</strong> {date || "—"} <br />
                <strong>Mode:</strong> {therapyModes.join(", ") || "—"} <br />
                {!isAgg && profileName && (
                  <>
                    <strong>Profile:</strong>{" "}
                    <span style={{ fontSize: "10px", color: "#4a5568" }}>
                      {profileName}
                    </span>{" "}
                    <br />
                  </>
                )}
                <strong>Cycle:</strong> {cycleInfo || "—"}
                {!isAgg && (
                  <>
                    , <strong>Osc:</strong> {oscillationStatus ? "ON" : "OFF"}
                  </>
                )}
              </div>
            </div>

            <div style={{ height: "1px", background: "#D1D5DB" }} />

            {/* SET VALUES - Now Second */}
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Set Values {isAgg && "(Avg.)"}
              </div>
              <div
                style={{
                  fontSize: responsive.fontSize,
                  color: "#1F2937",
                  lineHeight: 1.5,
                }}
              >
                <ColorLabel color="#A8E05F" text="Pause Pressure:" />{" "}
                {setPause?.toFixed(1) || "—"} cmH₂O <br />
                <ColorLabel color="#1FB7E3" text="Insp. Pressure:" />{" "}
                {setInsp?.toFixed(1) || "—"} cmH₂O <br />
                <ColorLabel color="#FF8F1C" text="Exp. Pressure:" />{" "}
                {setExp?.toFixed(1) || "—"} cmH₂O
              </div>
            </div>

            <div style={{ height: "1px", background: "#D1D5DB" }} />

            {/* MEASURED - Now Last (no inline legend) */}
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Measured {isAgg && "(Avg.)"}
              </div>
              <div
                style={{
                  fontSize: responsive.fontSize,
                  color: "#1F2937",
                  lineHeight: 1.5,
                }}
              >
                <ColorLabel
                  color={mergedConfig.colors.peakInsp}
                  text="Peak Pressure:"
                />{" "}
                {formatValue(peakInsp?.value, peakInsp?.min, peakInsp?.max)}{" "}
                cmH₂O <br />
                {/* Show vitals OR therapy metric */}
                {isVitalsParameter(selectedMetric) ? (
                  // Display vitals data
                  vitalData ? (
                    <>
                      <ColorLabel
                        color={getFieldConfig(selectedMetric).color}
                        text={`${vitalData.label}:`}
                      />{" "}
                      {formatValue(
                        vitalData.value,
                        vitalData.min,
                        vitalData.max,
                        true
                      )}{" "}
                      {vitalData.unit}
                    </>
                  ) : (
                    <>
                      <ColorLabel
                        color={getFieldConfig(selectedMetric).color}
                        text={`${getFieldConfig(selectedMetric).label}:`}
                      />{" "}
                      —
                    </>
                  )
                ) : (
                  // Display therapy metrics (existing code - unchanged)
                  <>
                    {selectedMetric === "flow" && (
                      <>
                        <ColorLabel
                          color={mergedConfig.colors.flow}
                          text="Peak Exp Flow:"
                        />{" "}
                        {formatValue(
                          peakFlow?.value,
                          peakFlow?.min,
                          peakFlow?.max
                        )}{" "}
                        lpm <br />
                      </>
                    )}
                    {selectedMetric === "volume" && (
                      <>
                        <ColorLabel
                          color={mergedConfig.colors.volume}
                          text="Exp Volume:"
                        />{" "}
                        {formatValue(
                          expVolume?.value,
                          expVolume?.min,
                          expVolume?.max
                        )}{" "}
                        ml
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Legend below tooltip - centered */}
          <MeasuredLegend />
        </div>
      );
    }

    // ============================================================================
    // DESKTOP/TABLET/LANDSCAPE LAYOUT (>=600px)
    // ============================================================================
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          boxSizing: "border-box",
        }}
      >
        {/* Row 1: Dropdown (for landscape and portrait modes) */}
        {(responsive.layout === "landscape" ||
          responsive.layout === "portrait") && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <CustomDropdown
                selected={selectedMetric}
                onChange={(value) => setSelectedMetric(value as MeasurementType)}
                options={MEASUREMENT_OPTIONS}
                minWidth={responsive.dropdownWidth}
              />
            </div>
          )}

        {/* Row 2: Boxes (and dropdown for desktop) */}
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            width: "100%",
            boxSizing: "border-box",
            paddingLeft: responsive.layout === "desktop" ? 20 : undefined,
          }}
        >
          {/* Box 1: Session Info - Now First */}
          <div
            style={{
              flex: `0 0 ${responsive.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: responsive.boxHeight,
              boxSizing: "border-box",
              marginRight: `${responsive.gapBetweenBoxes}px`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Session Info
            </div>
            <div
              style={{
                fontSize: responsive.fontSize,
                color: "#1F2937",
                lineHeight: 1.6,
              }}
            >
              <div style={{ marginBottom: "3px" }}>
                <strong>Time:</strong> {date || "—"}
              </div>
              <div style={{ marginBottom: "3px" }}>
                <strong>Mode:</strong> {therapyModes.join(", ") || "—"}
              </div>
              {!isAgg && profileName && (
                <div style={{ marginBottom: "3px" }}>
                  <strong>Profile:</strong>{" "}
                  <span style={{ fontSize: "10px", color: "#4a5568" }}>
                    {profileName}
                  </span>
                </div>
              )}
              <div>
                <strong>Cycle:</strong> {cycleInfo || "—"}
                {!isAgg && (
                  <>
                    , <strong>Osc:</strong> {oscillationStatus ? "ON" : "OFF"}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Box 2: Set Values - Now Second */}
          <div
            style={{
              flex: `0 0 ${responsive.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: responsive.boxHeight,
              boxSizing: "border-box",
              marginRight: `${responsive.gapBetweenBoxes}px`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Set Values {isAgg && "(Avg.)"}
            </div>
            <div
              style={{
                fontSize: responsive.fontSize,
                color: "#1F2937",
                lineHeight: 1.6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "3px",
                }}
              >
                <ColorLabel color="#A8E05F" text="Pause Pressure:" />
                <span style={{ marginLeft: responsive.valueSpacing }}>
                  {setPause?.toFixed(1) || "—"} cmH₂O
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "3px",
                }}
              >
                <ColorLabel color="#1FB7E3" text="Insp. Pressure:" />
                <span style={{ marginLeft: responsive.valueSpacing }}>
                  {setInsp?.toFixed(1) || "—"} cmH₂O
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <ColorLabel color="#FF8F1C" text="Exp. Pressure:" />
                <span style={{ marginLeft: responsive.valueSpacing }}>
                  {setExp?.toFixed(1) || "—"} cmH₂O
                </span>
              </div>
            </div>
          </div>

          {/* Box 3: Measured - Now Last (no inline legend) */}
          {/* Box 3: Measured - Now Last (no inline legend) */}
          <div
            style={{
              flex: `0 0 ${responsive.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: responsive.boxHeight,
              boxSizing: "border-box",
              marginRight:
                responsive.layout === "landscape" ||
                  responsive.layout === "portrait"
                  ? "0"
                  : `${responsive.gapBeforeDropdown}px`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Measured {isAgg && "(Avg.)"}
            </div>
            <div
              style={{
                fontSize: responsive.fontSize,
                color: "#1F2937",
                lineHeight: 1.6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "3px",
                }}
              >
                <ColorLabel
                  color={mergedConfig.colors.peakInsp}
                  text="Peak Pressure:"
                />
                <span style={{ marginLeft: responsive.valueSpacing }}>
                  {formatValue(peakInsp?.value, peakInsp?.min, peakInsp?.max)}{" "}
                  cmH₂O
                </span>
              </div>

              {/* Show vitals OR therapy metric */}
              {isVitalsParameter(selectedMetric) ? (
                // Display vitals data
                vitalData ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "3px",
                    }}
                  >
                    <ColorLabel
                      color={getFieldConfig(selectedMetric).color}
                      text={`${vitalData.label}:`}
                    />
                    <span style={{ marginLeft: responsive.valueSpacing }}>
                      {formatValue(
                        vitalData.value,
                        vitalData.min,
                        vitalData.max,
                        true
                      )}{" "}
                      {vitalData.unit}
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "3px",
                    }}
                  >
                    <ColorLabel
                      color={getFieldConfig(selectedMetric).color}
                      text={`${getFieldConfig(selectedMetric).label}:`}
                    />
                    <span style={{ marginLeft: responsive.valueSpacing }}>
                      —
                    </span>
                  </div>
                )
              ) : (
                // Display therapy metrics (existing code - unchanged)
                <>
                  {selectedMetric === "flow" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "3px",
                      }}
                    >
                      <ColorLabel
                        color={mergedConfig.colors.flow}
                        text="Peak Exp Flow:"
                      />
                      <span style={{ marginLeft: responsive.valueSpacing }}>
                        {formatValue(
                          peakFlow?.value,
                          peakFlow?.min,
                          peakFlow?.max
                        )}{" "}
                        lpm
                      </span>
                    </div>
                  )}
                  {selectedMetric === "volume" && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ColorLabel
                        color={mergedConfig.colors.volume}
                        text="Exp Volume:"
                      />
                      <span style={{ marginLeft: responsive.valueSpacing }}>
                        {formatValue(
                          expVolume?.value,
                          expVolume?.min,
                          expVolume?.max
                        )}{" "}
                        ml
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Dropdown (desktop only) */}
          {responsive.layout === "desktop" && (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                paddingBottom: 40,
              }}
            >
              <CustomDropdown
                selected={selectedMetric}
                onChange={(value) =>
                  setSelectedMetric(value as MeasurementType)
                }
                options={MEASUREMENT_OPTIONS}
                minWidth={responsive.dropdownWidth}
              />
            </div>
          )}
        </div>

        {/* Legend below tooltip boxes - centered */}
        <MeasuredLegend />
      </div>
    );
  };

  // ==================== MAIN COMPONENT RENDER ====================

  // Update the main render to include view buttons
  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <IonSpinner />
      </div>
    );
  // if (error) return <div style={{ padding: 16, color: "red" }}>{error}</div>;

  // REORDERED LAYOUT - Timeline at top, Info panel below timeline
  return (
    <IonCard className="graph-card ion-no-margin" style={{ paddingBottom: 12 }}>
      {/* Header with title and fullscreen button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px 0 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 className="patient-name">Therapy Details</h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            aria-label="Fullscreen"
            onClick={openFullscreen}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.8346 9.1665H15.8346V10.8332H19.168V14.1665H20.8346V9.1665ZM14.168 19.1665H10.8346V15.8332H9.16797V20.8332H14.168V19.1665Z"></path>
            </svg>
          </button>
        </div>
      </div>

      {error && <div style={{ padding: 16, color: "red" }}>{error}</div>}

      <IonCardContent style={{ padding: "4px 8px 8px 8px" }}>
        {!data || data.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: 24,
              color: "#666",
              fontSize: 16,
            }}
          >
            <IonIcon
              icon="analytics-outline"
              style={{ fontSize: 22, marginRight: 10 }}
            />
            No therapy data
          </div>
        ) : null}

        {/* Only show content if data is present */}
        {data && data.length > 0 && (
          <>
            {/* 1. Timeline Navigation - MOVED TO TOP */}
            <div style={{ marginTop: 0 }}>
              <TimelineSlider
                aggregationLevel={aggregationLevel}
                currentContext={currentContext}
                aggregatedData={
                  aggregationLevel === "yearly" ? yearlyData : monthlyData
                }
                therapySessions={therapySessions}
                selectedIndex={selectedTimelineIndex}
                onItemClick={handleTimelineItemClick}
                onWheelZoom={handleTimelineWheelZoom}
                colors={{
                  primary: mergedConfig.colors.setInsp || "#1FB7E3",
                  border: mergedConfig.colors.separator || "#e0e0e0",
                  disabled: mergedConfig.colors.bg || "#f0f0f0",
                  text: "#666",
                  textDisabled: "#ccc",
                }}
                onScrollTimeline={handleScrollTimeline}
                selectedDay={selectedDay}
                selectedMonth={selectedMonth}
                dataDateRange={{
                  min: parseLocalDate(dateRange.startDate),
                  max: parseLocalDate(dateRange.endDate),
                }}
                onTimelineScroll={handleTimelineScroll}
                isZooming={isZooming}
              />
            </div>

            {/* 2. Info panel - MOVED BELOW TIMELINE */}
            {/* <div style={{ marginTop: 8 }}>{renderInfoPanel()}</div> */}

            {/* 3. Main chart area */}
            {!isFullscreen && (
              <div
                style={{
                  width: "100%",
                  // height: mergedConfig.layout.mainHeight,
                  marginTop: 8,
                  // marginBottom: 50,
                }}
              >
                <ParentSize>{({ width }) => renderMainChart(width)}</ParentSize>
              </div>
            )}

            {/* 4. Context Label - you'll add below chart */}
          </>
        )}
      </IonCardContent>

      {/* Fullscreen Modal - SAME LAYOUT CHANGES */}
      <IonModal
        isOpen={isFullscreen}
        onDidDismiss={closeFullscreen}
        showBackdrop={false}
        backdropDismiss={false}
        className="fullscreen-page-modal"
        style={{
          "--width": "100vw",
          "--height": "100dvh",
          "--max-height": "100dvh",
          "--border-radius": "0",
          "--background": "#fff",
        }}
      >
        <div
          ref={modalRef}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            background: "#fff",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
            paddingTop: isPlatform("hybrid") ? "0" : "env(safe-area-inset-top)",
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Top bar */}
          <div
            style={{
              flexShrink: 0,
              position: "sticky",
              top: 0,
              zIndex: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: isMobilePhone ? "52px" : "auto",
              padding: "10px 16px",
              borderBottom: "1px solid #eee",
              background: "#fff",
              boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
            }}
          >
            {/* Close button */}
            <button
              onClick={closeFullscreen}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-label="Close fullscreen"
            >
              <IonIcon icon={close} style={{ fontSize: 24, color: "#333" }} />
            </button>
          </div>

          {/* Scrollable content area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: "100%",
                padding: "12px 16px 28px 16px",
                boxSizing: "border-box",
              }}
            >
              {/* 1. Timeline Navigation - MOVED TO TOP */}
              <div style={{ marginTop: 0 }}>
                <TimelineSlider
                  aggregationLevel={aggregationLevel}
                  currentContext={currentContext}
                  aggregatedData={
                    aggregationLevel === "yearly" ? yearlyData : monthlyData
                  }
                  therapySessions={therapySessions}
                  selectedIndex={selectedTimelineIndex}
                  onItemClick={handleTimelineItemClick}
                  onWheelZoom={handleTimelineWheelZoom}
                  colors={{
                    primary: mergedConfig.colors.setInsp || "#1FB7E3",
                    border: mergedConfig.colors.separator || "#e0e0e0",
                    disabled: mergedConfig.colors.bg || "#f0f0f0",
                    text: "#666",
                    textDisabled: "#ccc",
                  }}
                  onScrollTimeline={handleScrollTimeline}
                  selectedDay={selectedDay}
                  selectedMonth={selectedMonth}
                  dataDateRange={{
                    min: parseLocalDate(dateRange.startDate),
                    max: parseLocalDate(dateRange.endDate),
                  }}
                  onTimelineScroll={handleTimelineScroll}
                  isZooming={isZooming}
                />
              </div>

              {/* 2. Info Panel - MOVED BELOW TIMELINE */}
              {/* <div
                style={{
                  marginTop: 8,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {renderInfoPanel()}
              </div> */}

              {/* 3. Main Chart */}
              <div
                style={{
                  width: "100%",
                  // height: mergedConfig.layout.mainHeight,
                  height: "70vh",
                  overflow: "visible",
                  marginTop: 8,
                  paddingLeft: window.matchMedia(
                    "(orientation: landscape) and (max-width: 926px)"
                  ).matches
                    ? 24
                    : 0,
                }}
              >
                <ParentSize>
                  {({ width, height }) => renderMainChart(width, height)}
                </ParentSize>
              </div>

              {/* 4. Context Label - you'll add below chart */}
            </div>
          </div>
        </div>
      </IonModal>
    </IonCard>
  );
};

export default TherapyChart;
